import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, Tooltip, XAxis, YAxis, CartesianGrid} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { faBriefcase, faCircleCheck, faCircleXmark, faClock, faTriangleExclamation, faCircleInfo, faChevronRight, faChevronDown, faDownload, faArrowUp, faArrowDown, faCalendarDays} from '@fortawesome/free-solid-svg-icons';
import Grid from '@mui/material/Grid2';
import { useNavigate } from 'react-router-dom';
import { useJobImportDigest } from './hooks/useJobImportDigest';
import './jobreport.css';

/* ------------------------------------------------------------------ */
/*  Tokens & mock data                                                 */
/* ------------------------------------------------------------------ */

const CHANNEL = {
    apify: { label: 'Apify', sub: 'LinkedIn actor', color: '#C72B7A' },
    scrapper: { label: 'Scrapper', sub: null, color: '#1F5AA6' },
    bulk: { label: 'Bulk import', sub: null, color: '#B8790C' },
};

const STATUS = {
    active: '#C72B7A',
    inactive: '#8892A6',
    expiring: '#B8790C',
};

const COMPOSITION_PALETTE = ['#C72B7A', '#1F5AA6', '#B8790C', '#5E9C76', '#8E5FC9', '#DE5B95', '#3D8FB0', '#C98A2B'];
const COMPOSITION_UNKNOWN_COLOR = '#8892A6';

// Mirrors the exact filters on the job listing page (JobList.js), by column name,
// so "Source" here means the listing's `source` field (LinkedIn/Indeed/…), not
// the separate `source_of_job` ingestion-pipeline enum used in Job intake above.
const JOB_COMPOSITION = [
    {
        key: 'role_type', label: 'Role Type', field: 'role_type',
        data: [
            { label: 'Developer', value: 1402 },
            { label: 'Business Analyst', value: 562 },
            { label: 'Consultant', value: 421 },
            { label: 'Manager', value: 316 },
            { label: 'Product Owner', value: 246 },
            { label: 'Business System Analyst', value: 211 },
            { label: 'Developer/Administrator', value: 176 },
            { label: 'Admin', value: 105 },
            { label: 'Others', value: 73 },
        ],
    },
    {
        key: 'poster_type', label: 'Poster Type', field: 'poster_type',
        data: [
            { label: 'Staffing Company', value: 1897 },
            { label: 'Direct Requirement', value: 843 },
            { label: 'Implementation Partners', value: 456 },
            { label: 'Unknown', value: 316, unknown: true },
        ],
    },
    {
        key: 'site_type', label: 'Site Type', field: 'site_type',
        data: [
            { label: 'Remote', value: 1932 },
            { label: 'Hybrid', value: 984 },
            { label: 'Onsite', value: 526 },
            { label: 'Unknown', value: 70, unknown: true, filterValue: 'unknown' },
        ],
        note: 'Worksite currently resolves from a single location rule — worth widening once city/state parsing improves.',
    },
    {
        key: 'job_type', label: 'Job Type', field: 'job_type',
        data: [
            { label: 'Full-time', value: 2461 },
            { label: 'Contract', value: 738 },
            { label: 'Part-time', value: 176 },
            { label: 'Temporary', value: 88 },
            { label: 'Internship', value: 35 },
            { label: 'Unknown', value: 14, unknown: true, filterValue: 'unknown' },
        ],
    },
    {
        key: 'salary_type', label: 'Salary Type', field: 'salary_type',
        data: [
            { label: 'Yearly', value: 2180, filterValue: 'yearly' },
            { label: 'Hourly', value: 984, filterValue: 'hourly' },
            { label: 'Unknown', value: 348, unknown: true, filterValue: 'unknown' },
        ],
    },
    {
        key: 'source', label: 'Source', field: 'source',
        data: [
            { label: 'LinkedIn', value: 1265 },
            { label: 'TekP Intelligence', value: 949 },
            { label: 'Indeed', value: 456 },
            { label: 'Dice', value: 316 },
            { label: 'Monster', value: 211 },
            { label: 'Techfetch', value: 141 },
            { label: 'Glassdoor', value: 105 },
            { label: 'Other', value: 69 },
        ],
    },
    {
        key: 'experience_range', label: 'Experience Level', field: 'experience_range',
        data: [
            { label: '2 to 5 Years', value: 1123 },
            { label: '0 to 2 Years', value: 913 },
            { label: '5 to 8+ Years', value: 632 },
            { label: '8 to 12+ Years', value: 351 },
            { label: 'No experience', value: 246 },
            { label: '15+ Years', value: 176 },
            { label: 'Unknown', value: 71, unknown: true, filterValue: 'unknown' },
        ],
    },
    {
        key: 'citizenship_status', label: 'Citizenship Status', field: 'citizenship_status',
        data: [
            { label: 'USC', value: 1229 },
            { label: 'H1B', value: 843 },
            { label: 'OPT EAD', value: 456 },
            { label: 'GC', value: 316 },
            { label: 'H4 EAD', value: 246 },
            { label: 'GC EAD', value: 176 },
            { label: 'CPT', value: 105 },
            { label: 'L2 EAD', value: 70 },
            { label: 'Unknown', value: 71, unknown: true, filterValue: 'unknown' },
        ],
    },
];

// Maps each filter to the sessionStorage shape job-board/JobList.js restores its
// filter state from (see JobList.js SESSION_KEY) — this is how "click a bar,
// land on the listing pre-filtered" works without adding a new URL-param API.
// Admin-facing job board (route guarded to role admin/sub_admin) reads its
// filters from this key — the jobseeker-facing "jobBoardListState" key/route
// (/job-board/job-list) isn't reachable from here and 404s admins to /dashboard.
const JOB_LIST_SESSION_KEY = 'jobBoardAdminState';
const JOB_LIST_FILTER_FIELD = {
    role_type: { stateKey: 'selectedRoleTypes', multi: true },
    poster_type: { stateKey: 'selectedPosterTypes', multi: true },
    site_type: { stateKey: 'selectedSitesTypes', multi: true },
    job_type: { stateKey: 'selectedJobTypes', multi: true },
    salary_type: { stateKey: 'selectedSalaryTypes', multi: false },
    source: { stateKey: 'selectedSourceTypes', multi: true },
    experience_range: { stateKey: 'selectedExperience', multi: true },
    citizenship_status: { stateKey: 'selectedCityzenship', multi: true },
};

function seededRandom(seed) {
    let s = seed;
    return () => {
        s = (s * 9301 + 49297) % 233280;
        return s / 233280;
    };
}

function formatDayLabel(date) {
    return `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()}`;
}

const DAILY_INTAKE_SPAN_DAYS = 731; // ~2 full years, so "This Year" and "Last Year" always have real data


function buildDailyIntake() {
    const rand = seededRandom(42);
    const days = [];
    const today = stripTime(new Date());
    const start = addDays(today, -(DAILY_INTAKE_SPAN_DAYS - 1));
    for (let i = 0; i < DAILY_INTAKE_SPAN_DAYS; i++) {
        const d = addDays(start, i);
        const inWindow = i >= DAILY_INTAKE_SPAN_DAYS - 12; // most recent 12 days read as a growth surge
        const apify = inWindow ? 15 + rand() * 8 : 6 + rand() * 3;
        const scrapper = rand() > 0.82 ? 18 + rand() * 10 : 3 + rand() * 5;
        const bulk = inWindow ? 4 + rand() * 4 : 1 + rand() * 3;
        days.push({
            date: formatDayLabel(d),
            dateObj: d,
            apify: Math.round(apify),
            scrapper: Math.round(scrapper),
            bulk: Math.round(bulk),
        });
    }
    return days;
}

function aggregateByWeek(days) {
    const buckets = [];
    for (let i = 0; i < days.length; i += 7) {
        const chunk = days.slice(i, i + 7);
        const sums = sumChannels(chunk);
        buckets.push({
            date: formatDayLabel(chunk[0].dateObj),
            dateObj: chunk[0].dateObj,
            apify: sums.apify,
            scrapper: sums.scrapper,
            bulk: sums.bulk,
        });
    }
    return buckets;
}


function aggregateByMonth(days) {
    const order = [];
    const byKey = new Map();
    days.forEach((d) => {
        const key = `${d.dateObj.getFullYear()}-${d.dateObj.getMonth()}`;
        if (!byKey.has(key)) {
            const bucket = { dateObj: startOfMonth(d.dateObj), apify: 0, scrapper: 0, bulk: 0 };
            byKey.set(key, bucket);
            order.push(key);
        }
        const bucket = byKey.get(key);
        bucket.apify += d.apify;
        bucket.scrapper += d.scrapper;
        bucket.bulk += d.bulk;
    });
    return order.map((key) => {
        const bucket = byKey.get(key);
        return { ...bucket, date: bucket.dateObj.toLocaleString('en-US', { month: 'short', year: 'numeric' }) };
    });
}


function stripTime(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

function addDays(date, n) {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
}

function startOfWeek(date) {
    // Monday-based week
    const d = stripTime(date);
    const day = d.getDay(); // 0 = Sun ... 6 = Sat
    const diff = day === 0 ? -6 : 1 - day;
    return addDays(d, diff);
}

function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function fmtShort(date) {
    return `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()}, ${date.getFullYear()}`;
}

function toISODate(date) {
    const d = stripTime(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function parseISODate(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
}

function sumChannels(days) {
    return days.reduce(
        (acc, d) => ({
            apify: acc.apify + d.apify,
            scrapper: acc.scrapper + d.scrapper,
            bulk: acc.bulk + d.bulk,
            total: acc.total + d.apify + d.scrapper + d.bulk,
        }),
        { apify: 0, scrapper: 0, bulk: 0, total: 0 }
    );
}

function pctDelta(current, previous) {
    if (!previous) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 1000) / 10;
}

function startOfYear(date) {
    return new Date(date.getFullYear(), 0, 1);
}

function endOfYear(date) {
    return new Date(date.getFullYear(), 11, 31);
}


function resolveWindow(rangeLabel, today) {
    const t = stripTime(today);
    switch (rangeLabel) {
        case 'Last 24 Hours':
            return { start: t, end: t, prevStart: addDays(t, -1), prevEnd: addDays(t, -1) };
        case 'Last 2 Days':
            return { start: addDays(t, -1), end: t, prevStart: addDays(t, -3), prevEnd: addDays(t, -2) };
        case 'This Week': {
            const start = startOfWeek(t);
            const len = Math.round((t - start) / 86400000) + 1;
            return { start, end: t, prevStart: addDays(start, -7), prevEnd: addDays(start, -7 + len - 1) };
        }
        case 'Last Week': {
            const end = addDays(startOfWeek(t), -1);
            const start = addDays(end, -6);
            return { start, end, prevStart: addDays(start, -7), prevEnd: addDays(end, -7) };
        }
        case 'This Month': {
            const start = startOfMonth(t);
            const prevMonthStart = startOfMonth(addDays(start, -1));
            const dayOfMonth = t.getDate();
            const prevEnd = new Date(
                Math.min(addDays(prevMonthStart, dayOfMonth - 1).getTime(), endOfMonth(prevMonthStart).getTime())
            );
            return { start, end: t, prevStart: prevMonthStart, prevEnd };
        }
        case 'Last Month': {
            const thisMonthStart = startOfMonth(t);
            const start = startOfMonth(addDays(thisMonthStart, -1));
            const end = endOfMonth(start);
            const prevStart = startOfMonth(addDays(start, -1));
            const prevEnd = endOfMonth(prevStart);
            return { start, end, prevStart, prevEnd };
        }
        case 'This Year': {
            const start = startOfYear(t);
            const dayOfYear = Math.round((t - start) / 86400000);
            const prevStart = startOfYear(addDays(start, -1));
            const prevEnd = addDays(prevStart, dayOfYear);
            return { start, end: t, prevStart, prevEnd };
        }
        case 'Last Year': {
            const thisYearStart = startOfYear(t);
            const start = startOfYear(addDays(thisYearStart, -1));
            const end = endOfYear(start);
            const prevStart = startOfYear(addDays(start, -1));
            const prevEnd = endOfYear(prevStart);
            return { start, end, prevStart, prevEnd };
        }
        default:
            return { start: t, end: t, prevStart: addDays(t, -1), prevEnd: addDays(t, -1) };
    }
}

function daysInRange(allDays, start, end) {
    return allDays.filter((d) => d.dateObj >= start && d.dateObj <= end);
}

const VIEWS_PER_JOB = 30.9;
const SAVES_PER_JOB = 8.6;
const APPS_PER_JOB = 3.62;

function computeWindowStats(label, start, end, prevStart, prevEnd, allDays) {
    const windowDays = daysInRange(allDays, start, end);
    const prevWindowDays = daysInRange(allDays, prevStart, prevEnd);

    const current = sumChannels(windowDays);
    const previous = sumChannels(prevWindowDays);

    const rand = seededRandom(label.length * 97 + current.total * 3 + 11);
    const views = Math.round(current.total * VIEWS_PER_JOB * (0.94 + rand() * 0.12));
    const saves = Math.round(current.total * SAVES_PER_JOB * (0.94 + rand() * 0.12));
    const applications = Math.round(current.total * APPS_PER_JOB * (0.94 + rand() * 0.12));
    const prevViews = Math.round(previous.total * VIEWS_PER_JOB);
    const prevSaves = Math.round(previous.total * SAVES_PER_JOB);
    const prevApplications = Math.round(previous.total * APPS_PER_JOB);
    const rate = views > 0 ? (applications / views) * 100 : 0;
    const prevRate = prevViews > 0 ? (prevApplications / prevViews) * 100 : 0;

    return {
        label,
        start,
        end,
        days: windowDays,
        dayCount: windowDays.length,
        current,
        previous,
        deltaTotal: pctDelta(current.total, previous.total),
        jobs: current.total,
        jobsDelta: pctDelta(current.total, previous.total),
        views,
        viewsDelta: pctDelta(views, prevViews),
        saves,
        savesDelta: pctDelta(saves, prevSaves),
        applications,
        applicationsDelta: pctDelta(applications, prevApplications),
        rate,
        rateDelta: Math.round((rate - prevRate) * 10) / 10,
        dateRangeText: start.getTime() === end.getTime() ? fmtShort(start) : `${fmtShort(start)} – ${fmtShort(end)}`,
    };
}

function computeRangeStats(rangeLabel, allDays) {
    const today = allDays[allDays.length - 1].dateObj;
    const { start, end, prevStart, prevEnd } = resolveWindow(rangeLabel, today);
    return computeWindowStats(rangeLabel, start, end, prevStart, prevEnd, allDays);
}

function computeCustomRangeStats(start, end, allDays) {
    const lengthDays = Math.round((end - start) / 86400000) + 1;
    const prevEnd = addDays(start, -1);
    const prevStart = addDays(prevEnd, -(lengthDays - 1));
    return computeWindowStats('Custom Date', start, end, prevStart, prevEnd, allDays);
}

function buildSparkline(base, variance) {
    const rand = seededRandom(base * 7 + 3);
    return Array.from({ length: 14 }, (_, i) => ({
        i,
        v: Math.max(0, Math.round(base + Math.sin(i / 2) * variance + (rand() - 0.5) * variance)),
    }));
}

// Per-day views/saves/applications, derived from that day's job intake so
// busier intake days show busier engagement too — scoped to whatever date
// range the reader has selected, same as the stat cards and funnel above it.
function buildEngagementDaily(days) {
    return days.map((d) => {
        const total = d.apify + d.scrapper + d.bulk;
        const rand = seededRandom(Math.round(d.dateObj.getTime() / 86400000) * 31 + total * 13 + 7);
        return {
            date: formatDayLabel(d.dateObj),
            dateObj: d.dateObj,
            views: Math.round(total * VIEWS_PER_JOB * (0.82 + rand() * 0.36)),
            saves: Math.round(total * SAVES_PER_JOB * (0.82 + rand() * 0.36)),
            applications: Math.round(total * APPS_PER_JOB * (0.82 + rand() * 0.36)),
        };
    });
}

function aggregateEngagementByWeek(days) {
    const buckets = [];
    for (let i = 0; i < days.length; i += 7) {
        const chunk = days.slice(i, i + 7);
        buckets.push({
            date: formatDayLabel(chunk[0].dateObj),
            dateObj: chunk[0].dateObj,
            views: chunk.reduce((s, d) => s + d.views, 0),
            saves: chunk.reduce((s, d) => s + d.saves, 0),
            applications: chunk.reduce((s, d) => s + d.applications, 0),
        });
    }
    return buckets;
}

function aggregateEngagementByMonth(days) {
    const order = [];
    const byKey = new Map();
    days.forEach((d) => {
        const key = `${d.dateObj.getFullYear()}-${d.dateObj.getMonth()}`;
        if (!byKey.has(key)) {
            const bucket = { dateObj: startOfMonth(d.dateObj), views: 0, saves: 0, applications: 0 };
            byKey.set(key, bucket);
            order.push(key);
        }
        const bucket = byKey.get(key);
        bucket.views += d.views;
        bucket.saves += d.saves;
        bucket.applications += d.applications;
    });
    return order.map((key) => {
        const bucket = byKey.get(key);
        return { ...bucket, date: bucket.dateObj.toLocaleString('en-US', { month: 'short', year: 'numeric' }) };
    });
}

// Weekly "new records" counts from real created_at timestamps — used for the
// vendor company / recruiter growth trend, so growth here is honest, not a
// synthetic wave like some of the other mocked charts on this page.
function buildGrowthWeekly(rows, weeks) {
    const today = stripTime(new Date());
    const buckets = [];
    for (let w = weeks - 1; w >= 0; w--) {
        const weekStart = addDays(today, -(w * 7 + 6));
        const weekEnd = addDays(today, -(w * 7));
        buckets.push({
            date: formatDayLabel(weekStart),
            count: rows.filter((r) => r.createdAt >= weekStart && r.createdAt <= weekEnd).length,
        });
    }
    return buckets;
}

const DAILY_INTAKE = buildDailyIntake();
const DAILY_INTAKE_MIN_ISO = toISODate(DAILY_INTAKE[0].dateObj);
const DAILY_INTAKE_MAX_ISO = toISODate(DAILY_INTAKE[DAILY_INTAKE.length - 1].dateObj);

function buildWindowTable(activeRange) {
    return RANGES.map((label) => {
        const stats = computeRangeStats(label, DAILY_INTAKE);
        return {
            label,
            apify: stats.current.apify,
            scrapper: stats.current.scrapper,
            bulk: stats.current.bulk,
            total: stats.current.total,
            delta: stats.deltaTotal,
            active: label === activeRange,
        };
    });
}

const BULK_BATCHES = [
    { file: 'Aug_ServiceNow_batch.xlsx', when: 'Today, 9:14 AM', rows: 214, status: 'Imported' },
    { file: 'contract_roles_wk32.csv', when: 'Yesterday, 4:02 PM', rows: 89, status: 'Imported' },
    { file: 'vendor_upload_east.xlsx', when: 'Aug 9, 11:47 AM', rows: 156, status: 'Rolled back' },
];

const TOP_JOBS = [
    { title: 'Sr. ServiceNow Developer', id: 'JB004821', company: 'Meridian Data Group', channel: 'apify', views: 1940, applied: 312, conv: '16.1%' },
    { title: 'ServiceNow ITSM Consultant', id: 'JB004798', company: 'Vantage Systems Inc.', channel: 'apify', views: 1712, applied: 276, conv: '16.1%' },
    { title: 'Business Systems Analyst', id: 'JB004756', company: 'NorthPeak Technologies', channel: 'scrapper', views: 1205, applied: 204, conv: '16.9%' },
    { title: 'Owner, Platform', id: 'JB004732', company: 'BlueOrbit Consulting', channel: 'bulk', views: 968, applied: 151, conv: '15.6%' },
    { title: 'Solutions Architect', id: 'JB004709', company: 'Solstice IT Partners', channel: 'apify', views: 887, applied: 129, conv: '14.5%' },
];

// Same 5 states and colors as User::$jobSeekerStatuses on the backend, so a
// jobseeker's status pill reads identically here and on their profile.
const JOBSEEKER_STATUS = {
    progress: { label: 'In Progress', bg: '#B8D7FF', fg: '#1A5298' },
    completed: { label: 'Completed', bg: '#B1FFAF', fg: '#1D981A' },
    hold: { label: 'Hold', bg: '#FFDBB8', fg: '#C77321' },
    cancelled: { label: 'Cancelled', bg: '#FFD3D3', fg: '#F41818' },
    rejected: { label: 'Rejected', bg: '#FFEBEB', fg: '#D32F2F' },
};

const APPLICATION_JOBS = [
    { title: 'Sr. ServiceNow Developer', id: 'JB004821', channel: 'apify' },
    { title: 'ServiceNow ITSM Consultant', id: 'JB004798', channel: 'apify' },
    { title: 'Business Systems Analyst', id: 'JB004756', channel: 'scrapper' },
    { title: 'Owner, Platform', id: 'JB004732', channel: 'bulk' },
    { title: 'Solutions Architect', id: 'JB004709', channel: 'apify' },
    { title: 'ServiceNow HRSD Developer', id: 'JB004688', channel: 'apify' },
    { title: 'QA Automation Engineer', id: 'JB004651', channel: 'scrapper' },
];

const APPLICATION_FIRST_NAMES = ['Ava', 'Liam', 'Sophia', 'Noah', 'Isabella', 'Mason', 'Mia', 'Ethan', 'Amara', 'Lucas', 'Priya', 'Daniel', 'Grace', 'Ryan', 'Nina'];
const APPLICATION_LAST_NAMES = ['Carter', 'Nguyen', 'Patel', 'Schmidt', 'Rivera', 'Johnson', 'Kim', 'Okafor', 'Rossi', 'Brooks', 'Sharma', 'Bennett', 'Diaz', 'Fischer', 'Wallace'];
const APPLICATION_STATUS_KEYS = Object.keys(JOBSEEKER_STATUS);

// One row per (jobseeker, job) application — id, name, contact, pipeline status,
// which job and channel, and when — spread across the last ~150 days so every
// standard reporting window (this week/month/year) has something to show.
function buildApplications() {
    const rand = seededRandom(73);
    const today = stripTime(new Date());
    const rows = [];
    for (let i = 0; i < 140; i++) {
        const first = APPLICATION_FIRST_NAMES[Math.floor(rand() * APPLICATION_FIRST_NAMES.length)];
        const last = APPLICATION_LAST_NAMES[Math.floor(rand() * APPLICATION_LAST_NAMES.length)];
        const job = APPLICATION_JOBS[Math.floor(rand() * APPLICATION_JOBS.length)];
        const status = APPLICATION_STATUS_KEYS[Math.floor(rand() * APPLICATION_STATUS_KEYS.length)];
        const daysAgo = Math.floor(rand() * rand() * 150); // skewed recent, like real application activity
        rows.push({
            id: `AP${String(10000 + i)}`,
            jobseekerId: `JS${String(20450 + i)}`,
            name: `${first} ${last}`,
            email: `${first}.${last}`.toLowerCase() + '@example.com',
            phone: `(${400 + Math.floor(rand() * 500)}) 555-${String(1000 + Math.floor(rand() * 8999))}`,
            status,
            jobTitle: job.title,
            jobId: job.id,
            channel: job.channel,
            appliedAt: addDays(today, -daysAgo),
        });
    }
    return rows.sort((a, b) => b.appliedAt - a.appliedAt);
}

const APPLICATIONS = buildApplications();

function daysAgoLabel(days) {
    if (days <= 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
}

const VENDOR_COMPANY_PREFIXES = ['Meridian', 'Vantage', 'NorthPeak', 'BlueOrbit', 'Solstice', 'Crestline', 'Ashford', 'Highbridge', 'Lakeshore', 'Ironwood', 'Summit', 'Redwood', 'Falcon', 'Cobalt', 'Pinnacle', 'Silverline', 'Brightpath', 'Anchor', 'Cedarbrook', 'Vertex', 'Harborview', 'Greystone', 'Brookfield', 'Fernwood'];
const VENDOR_COMPANY_SUFFIXES = ['Data Group', 'Systems Inc.', 'Technologies', 'Consulting', 'Solutions', 'Partners', 'Staffing', 'Digital', 'Analytics', 'Ventures'];

// "Vendor companies" here is specifically the job_post_email_companies
// registry (EmailCompany) — the company side of the email-parsed intake
// pipeline. It has no relationship at all to Apify/scraped job postings,
// which live in a separate, unrelated JobPostCompany table this report
// doesn't touch — so "Jobs" below counts email-sourced postings only.
// created_at is real on this table (genuine "first seen"), so growth here
// is a real signal, unlike some of the other mocked-out totals on this page.
function buildVendorCompanies() {
    const rand = seededRandom(211);
    const today = stripTime(new Date());
    const rows = [];
    for (let i = 0; i < 96; i++) {
        const prefix = VENDOR_COMPANY_PREFIXES[Math.floor(rand() * VENDOR_COMPANY_PREFIXES.length)];
        const suffix = VENDOR_COMPANY_SUFFIXES[Math.floor(rand() * VENDOR_COMPANY_SUFFIXES.length)];
        const dbId = 200 + i;
        const jobs = Math.max(1, Math.round(rand() * rand() * 95));
        const applied = Math.round(jobs * (1.8 + rand() * 4.2));
        const lastActivityDays = Math.floor(rand() * rand() * 130);
        const createdDaysAgo = lastActivityDays + Math.floor(rand() * 320);
        rows.push({
            name: `${prefix} ${suffix}`,
            id: `VC${String(dbId).padStart(6, '0')}`,
            dbId,
            domain: `${prefix.toLowerCase()}${suffix.split(' ')[0].toLowerCase()}.com`,
            source: 'TekP Intelligence',
            jobs,
            applied,
            recruiters: 0, // filled in after recruiters are generated, below
            lastActivityDays,
            lastActivity: daysAgoLabel(lastActivityDays),
            createdAt: addDays(today, -createdDaysAgo),
            initials: (prefix[0] + suffix[0]).toUpperCase(),
            color: COMPOSITION_PALETTE[i % COMPOSITION_PALETTE.length],
        });
    }
    return rows;
}

const RECRUITER_USERNAMES = ['rkessler', 'jtorrence', 'smehta.talent', 'dcollins.rec', 'amartin_hr', 'tbrowning', 'lchen.talent', 'pgupta.rec', 'nwalsh_hr', 'ksanders.talent', 'rfeldman', 'jortiz.rec', 'cbennett_hr', 'mzhang.talent', 'hoconnor', 'vpatel.rec', 'edawson_hr', 'ykhan.talent', 'bmccarthy', 'gwerner.rec'];

// "Accuracy" is a confidence signal on the recruiter's own contact info, not
// a performance score — pipeline-created recruiters (from a real inbound
// recruiter email) are always set High automatically; manually admin-added
// ones default to Low until someone verifies them. That 72/28 split below
// mirrors that real behavior instead of an even random spread.
function buildRecruiters(companies) {
    const rand = seededRandom(307);
    const today = stripTime(new Date());
    const rows = [];
    for (let i = 0; i < 180; i++) {
        const company = companies[Math.floor(rand() * companies.length)];
        const isAuto = rand() < 0.72;
        const accuracy = isAuto ? 'High' : rand() < 0.5 ? 'Medium' : 'Low';
        const pct = accuracy === 'High' ? 90 + Math.round(rand() * 10) : accuracy === 'Medium' ? 55 + Math.round(rand() * 25) : 15 + Math.round(rand() * 30);
        const lastActivityDays = Math.floor(rand() * rand() * 160);
        const dbId = 5000 + i;
        const baseName = RECRUITER_USERNAMES[i % RECRUITER_USERNAMES.length];
        rows.push({
            name: i < RECRUITER_USERNAMES.length ? baseName : `${baseName}${Math.floor(i / RECRUITER_USERNAMES.length) + 1}`,
            id: `ITR${String(dbId).padStart(6, '0')}`,
            dbId,
            company: company.name,
            companyDbId: company.dbId,
            accuracy,
            pct,
            lastActivityDays,
            last: daysAgoLabel(lastActivityDays),
            createdAt: addDays(today, -(lastActivityDays + Math.floor(rand() * 260))),
            initials: baseName.slice(0, 2),
            color: COMPOSITION_PALETTE[i % COMPOSITION_PALETTE.length],
        });
    }
    return rows;
}

const VENDOR_COMPANIES = buildVendorCompanies();
const RECRUITERS = buildRecruiters(VENDOR_COMPANIES);
RECRUITERS.forEach((r) => {
    const company = VENDOR_COMPANIES.find((c) => c.dbId === r.companyDbId);
    if (company) company.recruiters += 1;
});

const RANGES = ['Last 24 Hours', 'Last 2 Days', 'This Week', 'Last Week', 'This Month', 'Last Month', 'This Year', 'Last Year'];

const WINDOW_DELTA_HINT = {
    'Last 24 Hours': 'vs the 24 hours before that',
    'Last 2 Days': 'vs the 2 days before that',
    'This Week': 'vs the same days last week',
    'Last Week': 'vs the week before',
    'This Month': 'vs the same days last month',
    'Last Month': 'vs the month before',
    'This Year': 'vs the same days last year',
    'Last Year': 'vs the year before',
};

const INTAKE_RANGE_OPTIONS = [...RANGES, 'Custom Date'];

const SECTIONS = [
    { id: 'digest', label: 'Apify scraping'},
    { id: 'inventory', label: 'Job inventory' },
    { id: 'intake', label: 'Job intake' },
    { id: 'engagement', label: 'Engagement' },
    { id: 'applications', label: 'Applications' },
    { id: 'network', label: 'Network' },
];

const DIGEST_SLOT_LABELS = {
    '09:00': '9:00 AM',
    '12:00': '12:00 PM',
    '14:00': '2:00 PM',
};

const DIGEST_STATUS_COLOR = {
    completed: '#1F5AA6',
    pending: '#B8790C',
    missed: '#C72B7A',
    failed: '#C72B7A',
    no_data: '#B8AEB5',
};

function digestSlotDetail(slot) {
    if (slot.status === 'completed') {
        const finished = slot.completed_at ? new Date(slot.completed_at.replace(' ', 'T')).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : null;
        return finished
            ? `Finished ${finished} · ${slot.records_fetched} fetched, ${slot.records_matched} matched`
            : `${slot.records_fetched} fetched, ${slot.records_matched} matched`;
    }
    if (slot.status === 'failed') return slot.error_message || 'Import failed';
    if (slot.status === 'missed') return 'No trigger recorded for this slot';
    if (slot.status === 'pending') return 'Not due yet';
    return 'Before digest tracking started';
}

function Delta({ value }) {
    const positive = value >= 0;
    return (
        <span className={`jbr-delta ${positive ? 'jbr-delta-up' : 'jbr-delta-down'}`}>
            <FontAwesomeIcon icon={positive ? faArrowUp : faArrowDown} style={{ fontSize: 9 }} />
            {Math.abs(value)}%
        </span>
    );
}

function Sparkline({ data, color }) {
    const gradientId = `jbr-spark-${color.replace('#', '')}`;
    return (
        <div className="jbr-sparkline">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                            <stop offset="100%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#${gradientId})`} isAnimationActive={false} />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}

function StatCard({ icon, iconBg, iconColor, label, value, delta, footnote, spark, sparkColor }) {
    return (
        <div className="jbr-stat-card">
            <div className="jbr-stat-top">
                <span className="jbr-stat-label">{label}</span>
                <span className="jbr-stat-icon" style={{ background: iconBg, color: iconColor }}>
                    <FontAwesomeIcon icon={icon} />
                </span>
            </div>
            <div className="jbr-stat-value-row">
                <span className="jbr-stat-value">{value}</span>
                {spark && <Sparkline data={spark} color={sparkColor} />}
            </div>
            <div className="jbr-stat-foot">
                {delta !== undefined && <Delta value={delta} />}
                <span className="jbr-stat-footnote">{footnote}</span>
            </div>
        </div>
    );
}

function InfoNote({ children }) {
    return (
        <div className="jbr-info-note">
            <FontAwesomeIcon icon={faCircleInfo} className="jbr-info-icon" />
            <p>{children}</p>
        </div>
    );
}

function DonutTooltip({ active, payload, total }) {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0];
    const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : '0.0';
    return (
        <div className="jbr-chart-tip">
            <span className="jbr-chart-tip-dot" style={{ background: d.payload.color }} />
            <span className="jbr-chart-tip-name">{d.name}</span>
            <span className="jbr-chart-tip-value">{Number(d.value).toLocaleString()} ({pct}%)</span>
        </div>
    );
}

function IntakeAreaTooltip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    const byKey = Object.fromEntries(payload.map((entry) => [entry.dataKey, entry]));
    return (
        <div className="jbr-area-tip">
            <p className="jbr-area-tip-date">{label}</p>
            {['apify', 'scrapper', 'bulk'].map((key) => {
                const entry = byKey[key];
                if (!entry) return null;
                return (
                    <div className="jbr-area-tip-row" key={key}>
                        <span className="jbr-area-tip-dot" style={{ background: CHANNEL[key].color }} />
                        <span className="jbr-area-tip-name">{CHANNEL[key].label}</span>
                        <span className="jbr-area-tip-value">{Number(entry.value).toLocaleString()}</span>
                    </div>
                );
            })}
        </div>
    );
}

function MiniAreaTooltip({ active, payload, label, color, name }) {
    if (!active || !payload || !payload.length) return null;
    return (
        <div className="jbr-area-tip">
            <p className="jbr-area-tip-date">{label}</p>
            <div className="jbr-area-tip-row">
                <span className="jbr-area-tip-dot" style={{ background: color }} />
                <span className="jbr-area-tip-name">{name}</span>
                <span className="jbr-area-tip-value">{Number(payload[0].value).toLocaleString()}</span>
            </div>
        </div>
    );
}

// One metric, its own scale — per the "never a dual-axis chart" rule, series at
// very different magnitudes (views vs. applications) get separate small
// multiples instead of being squashed onto a shared axis.
function EngagementTrendChart({ title, dataKey, color, gradientId, data, total, avgLabel, chartInterval }) {
    return (
        <div className="jbr-trend-mini">
            <div className="jbr-trend-mini-head">
                <span className="jbr-dot" style={{ background: color }} />
                <span className="jbr-trend-mini-title">{title}</span>
                <span className="jbr-trend-mini-total">{total.toLocaleString()}</span>
                <span className="jbr-trend-mini-avg">{avgLabel}</span>
            </div>
            <ResponsiveContainer width="100%" height={150}>
                <AreaChart data={data} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                            <stop offset="100%" stopColor={color} stopOpacity={0.04} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke="#EEF0F3" />
                    <XAxis
                        dataKey="date"
                        tick={{ fontSize: 9.5, fill: '#8892A6' }}
                        axisLine={false}
                        tickLine={false}
                        tickMargin={8}
                        height={20}
                        interval={chartInterval}
                    />
                    <YAxis
                        tick={{ fontSize: 9.5, fill: '#8892A6' }}
                        axisLine={false}
                        tickLine={false}
                        width={34}
                        tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : v)}
                    />
                    <Tooltip
                        content={<MiniAreaTooltip color={color} name={title} />}
                        cursor={{ stroke: '#E7BFD4', strokeWidth: 1 }}
                        wrapperStyle={{ outline: 'none' }}
                        allowEscapeViewBox={{ x: false, y: false }}
                    />
                    <Area type="monotone" dataKey={dataKey} stroke={color} fill={`url(#${gradientId})`} strokeWidth={2} isAnimationActive={false} />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}

function Donut({ segments, centerValue, centerLabel, size = 140, thickness = 20 }) {
    const data = segments.map((s) => ({ name: s.label, value: s.value, color: s.color }));
    const total = data.reduce((sum, d) => sum + d.value, 0);
    return (
        <div className="jbr-donut-wrap" style={{ width: size, height: size }}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Tooltip
                        content={<DonutTooltip total={total} />}
                        wrapperStyle={{ zIndex: 60, outline: 'none' }}
                        isAnimationActive={false}
                        allowEscapeViewBox={{ x: true, y: true }}
                        offset={10}
                    />
                    <Pie
                        data={data}
                        dataKey="value"
                        innerRadius={size / 2 - thickness}
                        outerRadius={size / 2}
                        paddingAngle={2}
                        startAngle={90}
                        endAngle={-270}
                        isAnimationActive={false}
                        stroke="none"
                    >
                        {data.map((d, i) => (
                            <Cell key={i} fill={d.color} />
                        ))}
                    </Pie>
                </PieChart>
            </ResponsiveContainer>
            <div className="jbr-donut-center">
                <span className="jbr-donut-value">{centerValue}</span>
                <span className="jbr-donut-label">{centerLabel}</span>
            </div>
        </div>
    );
}

function Bar({ pct, color, tooltip }) {
    return (
        <span className={`jbr-bar-tip${tooltip ? ' jbr-tip' : ''}`} data-tip={tooltip}>
            <div className="jbr-bar-track">
                <div className="jbr-bar-fill" style={{ width: `${pct}%`, background: color }} />
            </div>
        </span>
    );
}

function ChannelPill({ channel }) {
    const c = CHANNEL[channel];
    return (
        <span className="jbr-channel-pill" style={{ color: c.color, background: `${c.color}14` }}>
            <span className="jbr-channel-dot" style={{ background: c.color }} />
            {c.label}
        </span>
    );
}

function JobseekerStatusPill({ status }) {
    const s = JOBSEEKER_STATUS[status];
    if (!s) return <span className="jbr-muted-cell">—</span>;
    return (
        <span className="jbr-channel-pill" style={{ color: s.fg, background: s.bg }}>
            {s.label}
        </span>
    );
}

function Avatar({ initials, color, size = 32 }) {
    return (
        <span className="jbr-avatar" style={{ width: size, height: size, background: color, fontSize: size * 0.36 }}>
            {initials}
        </span>
    );
}

function RecencyBadge({ when }) {
    const commaIdx = when.indexOf(',');
    const day = commaIdx === -1 ? when : when.slice(0, commaIdx);
    const time = commaIdx === -1 ? '' : when.slice(commaIdx + 1).trim();
    const isToday = day === 'Today';
    const isYesterday = day === 'Yesterday';
    const isRecent = isToday || isYesterday;

    return (
        <span className="jbr-recency">
            <span className={`jbr-recency-pill ${isToday ? 'jbr-recency-today' : isYesterday ? 'jbr-recency-yesterday' : 'jbr-recency-older'}`}>
                {isToday && <span className="jbr-recency-dot" />}
                {!isRecent && <FontAwesomeIcon icon={faClock} style={{ fontSize: 8.5 }} />}
                {day}
            </span>
            {time && <span className="jbr-recency-time">{time}</span>}
        </span>
    );
}

function Section({ eyebrow, title, desc, children }) {
    return (
        <div className="jbr-section">
            <div className="jbr-section-body">
                {eyebrow && <p className="jbr-eyebrow">{eyebrow}</p>}
                {title && <h2 className="jbr-section-title">{title}</h2>}
                {desc && <p className="jbr-section-desc">{desc}</p>}
                {children}
            </div>
        </div>
    );
}

const DIGEST_STATUS_CHIP = {
    completed: { cls: 'jbr-chip-ok', icon: faCircleCheck, label: 'Completed' },
    pending: { cls: 'jbr-chip-pending', icon: faClock, label: 'Pending' },
    missed: { cls: 'jbr-chip-missed', icon: faTriangleExclamation, label: 'Missed' },
    failed: { cls: 'jbr-chip-missed', icon: faTriangleExclamation, label: 'Failed' },
    no_data: { cls: 'jbr-chip-muted', icon: faCircleInfo, label: 'No data' },
};

function StatusChip({ status }) {
    const c = DIGEST_STATUS_CHIP[status] ?? DIGEST_STATUS_CHIP.pending;
    return (
        <span className={`jbr-chip ${c.cls}`}>
            <FontAwesomeIcon icon={c.icon} style={{ fontSize: 10 }} />
            {c.label}
        </span>
    );
}

function CheckTimeline({ checks, onViewDetail }) {
    return (
        <div className="jbr-timeline">
            {checks.map((c, i) => {
                const isOk = c.status === 'completed';
                const iconCls = isOk ? 'jbr-timeline-icon-ok' : c.status === 'pending' ? 'jbr-timeline-icon-pending' : 'jbr-timeline-icon-missed';
                const icon = isOk ? faCircleCheck : c.status === 'pending' ? faClock : faTriangleExclamation;
                return (
                    <React.Fragment key={c.time}>
                        <div className="jbr-timeline-node">
                            <span className={`jbr-timeline-icon ${iconCls}`}>
                                <FontAwesomeIcon icon={icon} />
                            </span>
                            <div>
                                <p className="jbr-timeline-time">{c.label}</p>
                                <StatusChip status={c.status} />
                                <p className="jbr-timeline-detail">{c.detail}</p>
                                <p className="jbr-timeline-jobs">
                                    {c.jobs}
                                    <span>jobs added</span>
                                </p>
                                {onViewDetail && (
                                    <button type="button" className="jbr-link-btn" onClick={() => onViewDetail(c)}>
                                        View details
                                    </button>
                                )}
                            </div>
                        </div>
                        {i < checks.length - 1 && <div className="jbr-timeline-connector" />}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

function DigestDetailRow({ label, value, highlight }) {
    return (
        <div className={`jbr-detail-row${highlight ? ' jbr-detail-row-highlight' : ''}`}>
            <span>{label}</span>
            <span>{value}</span>
        </div>
    );
}

function DigestDetailModal({ title, subtitle, slots, onClose }) {
    return (
        <div className="jbr-modal-overlay" onClick={onClose}>
            <div className="jbr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="jbr-modal-head">
                    <div>
                        <h3 className="jbr-modal-title">{title}</h3>
                        {subtitle && <p className="jbr-modal-subtitle">{subtitle}</p>}
                    </div>
                    <button type="button" className="jbr-modal-close" onClick={onClose} aria-label="Close">
                        &times;
                    </button>
                </div>

                <div className="jbr-modal-body">
                    {slots.map((slot) => {
                        const label = DIGEST_SLOT_LABELS[slot.slot] ?? slot.slot;
                        const hasRun = slot.status === 'completed' || slot.status === 'failed';
                        const matchRate = slot.records_fetched > 0 ? Math.round((slot.records_matched / slot.records_fetched) * 100) : null;
                        const importRate = slot.records_matched > 0 ? Math.round((slot.records_imported / slot.records_matched) * 100) : null;
                        const duration = slot.triggered_at && slot.completed_at
                            ? Math.max(0, Math.round((new Date(slot.completed_at.replace(' ', 'T')) - new Date(slot.triggered_at.replace(' ', 'T'))) / 60000))
                            : null;

                        return (
                            <div className="jbr-modal-slot" key={slot.slot}>
                                <div className="jbr-modal-slot-head">
                                    <span className="jbr-modal-slot-label">{label}</span>
                                    <StatusChip status={slot.status} />
                                </div>

                                {!hasRun && (
                                    <p className="jbr-card-sub" style={{ margin: 0 }}>
                                        {slot.status === 'missed' && 'No trigger was recorded for this slot.'}
                                        {slot.status === 'pending' && 'This slot has not run yet today.'}
                                        {slot.status === 'no_data' && 'This is before import tracking started.'}
                                    </p>
                                )}

                                {slot.status === 'failed' && slot.error_message && (
                                    <div className="jbr-modal-error">{slot.error_message}</div>
                                )}

                                {hasRun && (
                                    <>
                                        <DigestDetailRow label="Fetched from Apify" value={slot.records_fetched.toLocaleString()} />
                                        <DigestDetailRow label="Matched (ServiceNow titles)" value={`${slot.records_matched.toLocaleString()}${matchRate !== null ? ` (${matchRate}%)` : ''}`} />
                                        <DigestDetailRow label="Imported to job board" value={slot.records_imported.toLocaleString()} highlight />
                                        <DigestDetailRow label="Skipped — already exists" value={slot.records_duplicate.toLocaleString()} />
                                        <DigestDetailRow label="Skipped — failed validation" value={slot.records_failed.toLocaleString()} />
                                        <DigestDetailRow label="New companies added" value={slot.companies_added.toLocaleString()} />
                                        <DigestDetailRow label="New vendors added" value={slot.vendors_added.toLocaleString()} />
                                        {importRate !== null && <DigestDetailRow label="Import yield" value={`${importRate}% of matched rows`} />}
                                        {duration !== null && <DigestDetailRow label="Run time" value={`${duration} min`} />}
                                        {slot.triggered_at && (
                                            <DigestDetailRow
                                                label="Triggered / Completed"
                                                value={`${new Date(slot.triggered_at.replace(' ', 'T')).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} → ${slot.completed_at ? new Date(slot.completed_at.replace(' ', 'T')).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—'}`}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function DigestSection({ digest, loading, error }) {
    const todayLabel = useMemo(() => fmtShort(new Date()), []);
    const [detailModal, setDetailModal] = useState(null);

    const slots = digest?.slots ?? [];
    const history = digest?.history ?? [];

    const checks = slots.map((s) => ({
        time: s.slot,
        label: DIGEST_SLOT_LABELS[s.slot] ?? s.slot,
        status: s.status,
        detail: digestSlotDetail(s),
        jobs: s.records_imported,
        raw: s,
    }));

    return (
        <Section>
            {loading && !digest && <div className="jbr-card jbr-timeline-card">Loading today's import digest…</div>}
            {error && <div className="jbr-card jbr-timeline-card">{error}</div>}

            {digest && (
                <>
                    <div className="jbr-card jbr-timeline-card">
                        <h3 className="jbr-card-title">Today's checks</h3>
                        <p className="jbr-card-sub">{todayLabel}</p>
                        <CheckTimeline
                            checks={checks}
                            onViewDetail={(c) => setDetailModal({ title: c.label, subtitle: todayLabel, slots: [c.raw] })}
                        />
                    </div>

                    <div className="jbr-card jbr-history-card">
                        <h3 className="jbr-card-title jbr-history-title">14-day check history</h3>
                        <p className="jbr-card-sub">
                            {todayLabel} back to {history[history.length - 1]?.date ? fmtShort(new Date(`${history[history.length - 1].date}T00:00:00`)) : ''} · every scheduled check
                        </p>

                        <div className="jbr-legend-inline">
                            <span className="jbr-legend-inline-item">
                                <span className="jbr-dot" style={{ background: DIGEST_STATUS_COLOR.completed }} /> Completed
                            </span>
                            <span className="jbr-legend-inline-item">
                                <span className="jbr-dot" style={{ background: DIGEST_STATUS_COLOR.pending }} /> Pending
                            </span>
                            <span className="jbr-legend-inline-item">
                                <span className="jbr-dot" style={{ background: DIGEST_STATUS_COLOR.missed }} /> Missed / Failed
                            </span>
                        </div>

                        <div className="jbr-table-scroll">
                            <table className="jbr-table jbr-history-table">
                                <thead>
                                    <tr>
                                        <th>Day</th>
                                        <th>Completed</th>
                                        <th>Missed</th>
                                        <th>Failed</th>
                                        <th style={{ textAlign: 'right' }}>Imported</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((row, i) => {
                                        const dayLabel = i === 0 ? 'Today' : fmtShort(new Date(`${row.date}T00:00:00`));
                                        return (
                                            <tr
                                                key={row.date}
                                                className={`jbr-history-row-clickable ${i === 0 ? 'jbr-history-row-today' : ''}`}
                                                onClick={() => setDetailModal({ title: dayLabel, subtitle: fmtShort(new Date(`${row.date}T00:00:00`)), slots: row.slots })}
                                            >
                                                <td className={i === 0 ? 'jbr-history-day-today' : ''}>{dayLabel}</td>
                                                <td>{row.completed}</td>
                                                <td style={row.missed > 0 ? { color: 'var(--jbr-primary)', fontWeight: 700 } : undefined}>{row.missed}</td>
                                                <td style={row.failed > 0 ? { color: 'var(--jbr-primary)', fontWeight: 700 } : undefined}>{row.failed}</td>
                                                <td className="jbr-mono jbr-history-total" style={{ textAlign: 'right' }}>
                                                    <span className="jbr-history-total-value">{row.records_imported}</span>
                                                    <span className="jbr-history-total-label">jobs</span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <InfoNote>
                            Click any day for a full fetched/matched/imported/skipped breakdown. A missed or failed slot is called out here immediately, and a detailed breakdown is emailed daily at 11:55 PM to the configured recipients.
                        </InfoNote>
                    </div>

                    {detailModal && (
                        <DigestDetailModal
                            title={detailModal.title}
                            subtitle={detailModal.subtitle}
                            slots={detailModal.slots}
                            onClose={() => setDetailModal(null)}
                        />
                    )}
                </>
            )}
        </Section>
    );
}

function JobCompositionCard() {
    const [activeKey, setActiveKey] = useState(JOB_COMPOSITION[0].key);
    const dim = JOB_COMPOSITION.find((d) => d.key === activeKey);

    const rows = useMemo(() => [...dim.data].sort((a, b) => b.value - a.value), [dim]);
    const total = rows.reduce((sum, r) => sum + r.value, 0) || 1;
    const max = rows[0]?.value || 1;
    const unknownRow = rows.find((r) => r.unknown);
    const unknownPct = unknownRow ? Math.round((unknownRow.value / total) * 100) : 0;

    const openInJobList = (row) => {
        const target = JOB_LIST_FILTER_FIELD[dim.field];
        if (!target) return;
        const value = row.filterValue || row.label;
        const state = { [target.stateKey]: target.multi ? [value] : value };
        try {
            sessionStorage.setItem(JOB_LIST_SESSION_KEY, JSON.stringify(state));
        } catch (err) {
            // sessionStorage unavailable (private mode etc.) — still open, just without the pre-applied filter
        }
        // window.open (not navigate) so the report stays open in this tab — sessionStorage
        // is cloned into script-opened tabs per spec, so the filter still carries over.
        window.open('/job-board', '_blank');
    };

    return (
        <div className="jbr-card">
            <h3 className="jbr-card-title">Job composition</h3>
            <p className="jbr-card-sub">Active jobs by {dim.label.toLowerCase()} — same filters as the job listing page. Click a row to open it filtered in a new tab.</p>

            <div className="jbr-pill-tabs">
                {JOB_COMPOSITION.map((d) => (
                    <button
                        key={d.key}
                        type="button"
                        className={`jbr-pill-tab ${activeKey === d.key ? 'jbr-pill-tab-active' : ''}`}
                        onClick={() => setActiveKey(d.key)}
                    >
                        {d.label}
                    </button>
                ))}
            </div>

            <div className="jbr-bars">
                {rows.map((row, i) => {
                    const barPct = Math.round((row.value / max) * 100);
                    const sharePct = ((row.value / total) * 100).toFixed(1);
                    const color = row.unknown ? COMPOSITION_UNKNOWN_COLOR : COMPOSITION_PALETTE[i % COMPOSITION_PALETTE.length];
                    return (
                        <div
                            className="jbr-comp-bar-row jbr-comp-bar-row-clickable"
                            key={row.label}
                            role="button"
                            tabIndex={0}
                            onClick={() => openInJobList(row)}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openInJobList(row)}
                            title={`View ${row.label} jobs on the job listing page`}
                        >
                            <span className="jbr-comp-bar-label">{row.label}</span>
                            <Bar pct={barPct} color={color} tooltip={`${row.label} · ${row.value.toLocaleString()} jobs (${sharePct}%)`} />
                            <span className="jbr-comp-bar-value">
                                {row.value.toLocaleString()} <em>{sharePct}%</em>
                            </span>
                        </div>
                    );
                })}
            </div>

            {unknownRow && unknownPct > 0 && (
                <InfoNote>
                    <strong>{unknownPct}%</strong> of active jobs have no {dim.label.toLowerCase()} recorded — mostly
                    scraped or bulk-imported postings. Worth backfilling for cleaner filtering.
                </InfoNote>
            )}

            {dim.note && <InfoNote>{dim.note}</InfoNote>}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Section 1 — Job inventory                                          */
/* ------------------------------------------------------------------ */

function JobInventorySection() {
    return (
        <Section>
            <div className="jbr-stat-grid">
                <StatCard icon={faBriefcase} iconBg="rgba(199,43,122,0.1)" iconColor="#C72B7A" label="Total jobs" value="4,820" delta={7.4} footnote="All-time postings" />
                <StatCard icon={faCircleCheck} iconBg="rgba(31,90,166,0.1)" iconColor="#1F5AA6" label="Active jobs" value="3,512" footnote="Currently live" />
                <StatCard icon={faCircleXmark} iconBg="rgba(136,146,166,0.14)" iconColor="#6B7280" label="Inactive jobs" value="1,308" footnote="Auto-inactivated at 90 days" />
                <StatCard icon={faClock} iconBg="rgba(184,121,12,0.12)" iconColor="#B8790C" label="Approaching 90-day cutoff" value="184" footnote="Published 76–90 days ago" />
            </div>

            <div className="jbr-callout">
                <FontAwesomeIcon icon={faTriangleExclamation} className="jbr-callout-icon" />
                <p>
                    <strong>184 active jobs</strong> are 76–90 days old and will be auto-marked Inactive by the daily cleanup once they pass 90 days.
                </p>
                <button className="jbr-btn jbr-btn-ghost">
                    Review list <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 11 }} />
                </button>
            </div>

            <div className="jbr-stack">
                <div className="jbr-card">
                    <h3 className="jbr-card-title">Active jobs by posting age</h3>
                    <p className="jbr-card-sub">Days since published · active jobs only, 90-day auto-inactive cutoff</p>

                    <div className="jbr-bars">
                        {[
                            { label: '0–30 days', value: 2037, pct: 100, color: STATUS.active },
                            { label: '31–60 days', value: 843, pct: 41, color: '#DE5B95' },
                            { label: '61–75 days', value: 448, pct: 22, color: '#F2BAD3' },
                            { label: '76–90 days', value: 184, pct: 9, color: STATUS.expiring },
                        ].map((row) => (
                            <div className="jbr-bar-row" key={row.label}>
                                <span className="jbr-bar-label">{row.label}</span>
                                <Bar pct={row.pct} color={row.color} tooltip={`${row.label} · ${row.value.toLocaleString()} active jobs`} />
                                <span className="jbr-bar-value">{row.value}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <JobCompositionCard />
            </div>
        </Section>
    );
}

function RangeDropdown({ value, customRange, onSelect, onApplyCustom }) {
    const [open, setOpen] = useState(false);
    const [draftStart, setDraftStart] = useState(customRange?.startISO || DAILY_INTAKE_MAX_ISO);
    const [draftEnd, setDraftEnd] = useState(customRange?.endISO || DAILY_INTAKE_MAX_ISO);
    const rootRef = useRef(null);

    useEffect(() => {
        function handleOutsideClick(e) {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        }
        function handleEscape(e) {
            if (e.key === 'Escape') setOpen(false);
        }
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    const triggerLabel = value === 'Custom Date' && customRange ? customRange.label : value;
    const isCustomInvalid = draftStart && draftEnd && draftStart > draftEnd;

    return (
        <div className="jbr-dropdown" ref={rootRef}>
            <button
                type="button"
                className={`jbr-dropdown-trigger ${open ? 'jbr-dropdown-trigger-open' : ''}`}
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <FontAwesomeIcon icon={faCalendarDays} className="jbr-dropdown-trigger-icon" />
                <span className="jbr-dropdown-trigger-label">{triggerLabel}</span>
                <FontAwesomeIcon icon={faChevronDown} className={`jbr-dropdown-caret ${open ? 'jbr-dropdown-caret-open' : ''}`} />
            </button>

            {open && (
                <div className="jbr-dropdown-panel" role="listbox">
                    <ul className="jbr-dropdown-list">
                        {INTAKE_RANGE_OPTIONS.map((option) => (
                            <li key={option}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={value === option}
                                    className={`jbr-dropdown-item ${value === option ? 'jbr-dropdown-item-active' : ''}`}
                                    onClick={() => {
                                        onSelect(option);
                                        if (option !== 'Custom Date') setOpen(false);
                                    }}
                                >
                                    {option}
                                    {value === option && <FontAwesomeIcon icon={faCircleCheck} className="jbr-dropdown-item-check" />}
                                </button>
                            </li>
                        ))}
                    </ul>

                    {value === 'Custom Date' && (
                        <div className="jbr-dropdown-custom">
                            <div className="jbr-dropdown-custom-fields">
                                <label className="jbr-dropdown-custom-field">
                                    <span>From</span>
                                    <input
                                        type="date"
                                        value={draftStart}
                                        min={DAILY_INTAKE_MIN_ISO}
                                        max={DAILY_INTAKE_MAX_ISO}
                                        onChange={(e) => setDraftStart(e.target.value)}
                                    />
                                </label>
                                <label className="jbr-dropdown-custom-field">
                                    <span>To</span>
                                    <input
                                        type="date"
                                        value={draftEnd}
                                        min={DAILY_INTAKE_MIN_ISO}
                                        max={DAILY_INTAKE_MAX_ISO}
                                        onChange={(e) => setDraftEnd(e.target.value)}
                                    />
                                </label>
                            </div>
                            {isCustomInvalid && <p className="jbr-dropdown-custom-error">"From" must be before "To".</p>}
                            <button
                                type="button"
                                className="jbr-btn jbr-btn-primary jbr-btn-sm jbr-dropdown-apply"
                                disabled={!draftStart || !draftEnd || isCustomInvalid}
                                onClick={() => {
                                    onApplyCustom(draftStart, draftEnd);
                                    setOpen(false);
                                }}
                            >
                                Apply range
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Section 2 — Job intake                                             */
/* ------------------------------------------------------------------ */

function JobIntakeSection() {
    const [range, setRange] = useState('This Month');
    const [customRange, setCustomRange] = useState(null); // { start, end, startISO, endISO, label }

    const handleApplyCustom = (startISO, endISO) => {
        const start = parseISODate(startISO);
        const end = parseISODate(endISO);
        setCustomRange({
            start,
            end,
            startISO,
            endISO,
            label: start.getTime() === end.getTime() ? fmtShort(start) : `${fmtShort(start)} – ${fmtShort(end)}`,
        });
    };

    const stats = useMemo(() => {
        if (range === 'Custom Date' && customRange) {
            return computeCustomRangeStats(customRange.start, customRange.end, DAILY_INTAKE);
        }
        return computeRangeStats(range === 'Custom Date' ? 'This Month' : range, DAILY_INTAKE);
    }, [range, customRange]);

    const windowTable = useMemo(() => buildWindowTable(range), [range]);
    const rangeDisplayLabel = range === 'Custom Date' && customRange ? customRange.label : range;
    const chartGranularity = useMemo(() => {
        if (range === 'Last Month') return 'week';
        if (range === 'This Year' || range === 'Last Year') return 'month';
        if (range === 'Custom Date') {
            if (stats.dayCount > 120) return 'month';
            if (stats.dayCount > 31) return 'week';
        }
        return 'day';
    }, [range, stats.dayCount]);

    const chartData = useMemo(() => {
        const base = stats.days.length > 0 ? stats.days : DAILY_INTAKE.slice(-1);
        if (chartGranularity === 'month') return aggregateByMonth(base);
        if (chartGranularity === 'week') return aggregateByWeek(base);
        return base;
    }, [stats.days, chartGranularity]);

    const chartInterval = chartGranularity === 'day' ? Math.max(0, Math.ceil(chartData.length / 7) - 1) : 0;

    const channelTotal = stats.current.total || 1;
    const channelShare = [
        { key: 'apify', label: CHANNEL.apify.label, value: stats.current.apify, color: CHANNEL.apify.color },
        { key: 'scrapper', label: CHANNEL.scrapper.label, value: stats.current.scrapper, color: CHANNEL.scrapper.color },
        { key: 'bulk', label: CHANNEL.bulk.label, value: stats.current.bulk, color: CHANNEL.bulk.color },
    ];

    const spark = (base, variance) => buildSparkline(Math.max(base, 1), Math.max(variance, 1));

    return (
        <Section>
            <div className="jbr-section-head-row jbr-intake-head-row">
                <p className="jbr-showing-line jbr-showing-line-inline">
                    Showing <strong>{rangeDisplayLabel}</strong> · {stats.dateRangeText} · {stats.dayCount} day{stats.dayCount === 1 ? '' : 's'}
                </p>
                <RangeDropdown value={range} customRange={customRange} onSelect={setRange} onApplyCustom={handleApplyCustom} />
            </div>

            {range === 'Custom Date' && stats.dayCount === 0 && (
                <InfoNote>
                    No intake data falls inside <strong>{rangeDisplayLabel}</strong>. Try a range between{' '}
                    <strong>{fmtShort(DAILY_INTAKE[0].dateObj)}</strong> and <strong>{fmtShort(DAILY_INTAKE[DAILY_INTAKE.length - 1].dateObj)}</strong>.
                </InfoNote>
            )}

            <div className="jbr-stat-grid">
                <StatCard icon={faBriefcase} iconBg="rgba(199,43,122,0.1)" iconColor="#C72B7A" label="New jobs posted" value={stats.jobs.toLocaleString()} delta={stats.jobsDelta} spark={spark(stats.jobs / Math.max(stats.dayCount, 1), 6)} sparkColor="#C72B7A" />
                <StatCard icon={faBriefcase} iconBg={`${CHANNEL.apify.color}1A`} iconColor={CHANNEL.apify.color} label="From Apify" value={stats.current.apify.toLocaleString()} footnote={`${((stats.current.apify / channelTotal) * 100).toFixed(0)}% of this window`} />
                <StatCard icon={faBriefcase} iconBg={`${CHANNEL.scrapper.color}1A`} iconColor={CHANNEL.scrapper.color} label="From Scrapper" value={stats.current.scrapper.toLocaleString()} footnote={`${((stats.current.scrapper / channelTotal) * 100).toFixed(0)}% of this window`} />
                <StatCard icon={faBriefcase} iconBg={`${CHANNEL.bulk.color}1A`} iconColor={CHANNEL.bulk.color} label="From bulk import" value={stats.current.bulk.toLocaleString()} footnote={`${((stats.current.bulk / channelTotal) * 100).toFixed(0)}% of this window`} />
            </div>

            <div className="jbr-grid-2 jbr-grid-2-wide">
                <div className="jbr-card">
                    <h3 className="jbr-card-title">Daily intake — {rangeDisplayLabel}</h3>
                    <p className="jbr-card-sub">
                        Stacked by channel · {stats.dateRangeText}
                        {chartGranularity !== 'day' && ` · ${chartGranularity}ly totals`}
                    </p>
                    <div className="jbr-legend-inline">
                        {Object.entries(CHANNEL).map(([key, c]) => (
                            <span key={key} className="jbr-legend-inline-item">
                                <span className="jbr-dot" style={{ background: c.color }} />
                                {c.label} {c.sub && <em>({c.sub})</em>}
                            </span>
                        ))}
                    </div>
                    <div className="jbr-area-wrap">
                        <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                                <defs>
                                    <linearGradient id="jbrFillApify" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={CHANNEL.apify.color} stopOpacity={0.5} />
                                        <stop offset="100%" stopColor={CHANNEL.apify.color} stopOpacity={0.04} />
                                    </linearGradient>
                                    <linearGradient id="jbrFillScrapper" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={CHANNEL.scrapper.color} stopOpacity={0.5} />
                                        <stop offset="100%" stopColor={CHANNEL.scrapper.color} stopOpacity={0.04} />
                                    </linearGradient>
                                    <linearGradient id="jbrFillBulk" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={CHANNEL.bulk.color} stopOpacity={0.5} />
                                        <stop offset="100%" stopColor={CHANNEL.bulk.color} stopOpacity={0.04} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid vertical={false} stroke="#EEF0F3" />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 10, fill: '#8892A6' }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickMargin={10}
                                    height={28}
                                    interval={chartInterval}
                                    padding={{ left: 28, right: 28 }}
                                />
                                <YAxis hide width={0} />
                                <Tooltip
                                    content={<IntakeAreaTooltip />}
                                    cursor={{ stroke: '#E7BFD4', strokeWidth: 1 }}
                                    wrapperStyle={{ outline: 'none' }}
                                    allowEscapeViewBox={{ x: false, y: false }}
                                />
                                <Area type="monotone" dataKey="apify" stackId="1" stroke={CHANNEL.apify.color} fill="url(#jbrFillApify)" strokeWidth={1.5} isAnimationActive={false} />
                                <Area type="monotone" dataKey="scrapper" stackId="1" stroke={CHANNEL.scrapper.color} fill="url(#jbrFillScrapper)" strokeWidth={1.5} isAnimationActive={false} />
                                <Area type="monotone" dataKey="bulk" stackId="1" stroke={CHANNEL.bulk.color} fill="url(#jbrFillBulk)" strokeWidth={1.5} isAnimationActive={false} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    <InfoNote>
                        The Apify actor is currently scoped to <strong>title contains "ServiceNow"</strong> — widening that
                        filter is the fastest lever to grow this channel's volume.
                    </InfoNote>
                </div>

                <div className="jbr-stack">
                    <div className="jbr-card">
                        <h3 className="jbr-card-title">Channel share</h3>
                        <p className="jbr-card-sub">{rangeDisplayLabel}</p>
                        <div className="jbr-donut-row">
                            <Donut
                                size={126}
                                thickness={19}
                                centerValue={stats.jobs.toLocaleString()}
                                centerLabel="JOBS"
                                segments={channelShare.map((c) => ({ label: c.label, value: c.value, color: c.color }))}
                            />
                            <div className="jbr-legend-list">
                                {channelShare.map((c) => (
                                    <div className="jbr-legend-row" key={c.key}>
                                        <span className="jbr-dot" style={{ background: c.color }} />
                                        <span className="jbr-legend-name">{c.label}</span>
                                        <span className="jbr-legend-value">{c.value.toLocaleString()}</span>
                                        <span className="jbr-legend-pct">{((c.value / channelTotal) * 100).toFixed(1)}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="jbr-card">
                        <h3 className="jbr-card-title">Recent bulk-import batches</h3>
                        <p className="jbr-card-sub">All-or-nothing per file</p>
                        <table className="jbr-mini-table">
                            <thead>
                                <tr>
                                    <th>File</th>
                                    <th>Rows</th>
                                    <th>Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {BULK_BATCHES.map((b) => (
                                    <tr key={b.file}>
                                        <td>
                                            <span className="jbr-cell-title">{b.file}</span>
                                            <RecencyBadge when={b.when} />
                                        </td>
                                        <td className="jbr-mono">{b.rows}</td>
                                        <td>
                                            <span className={`jbr-status-pill ${b.status === 'Imported' ? 'jbr-status-good' : 'jbr-status-bad'}`}>
                                                {b.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="jbr-card">
                <h3 className="jbr-card-title">Every window, at a glance</h3>
                <p className="jbr-card-sub">No need to switch filters — all standard reporting windows, side by side</p>
                <div className="jbr-table-scroll">
                    <table className="jbr-table">
                        <thead>
                            <tr>
                                <th>Window</th>
                                <th>Apify</th>
                                <th>Scrapper</th>
                                <th>Bulk import</th>
                                <th>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {windowTable.map((row) => (
                                <tr
                                    key={row.label}
                                    className={row.active ? 'jbr-row-active' : ''}
                                    onClick={() => setRange(row.label)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td className={row.active ? 'jbr-row-label-active' : ''}>{row.label}</td>
                                    <td className="jbr-mono">{row.apify}</td>
                                    <td className="jbr-mono">{row.scrapper}</td>
                                    <td className="jbr-mono">{row.bulk}</td>
                                    <td className="jbr-mono jbr-total-cell">
                                        <strong>{row.total}</strong>
                                        <span
                                            className={`jbr-tiny-delta ${row.delta >= 0 ? 'jbr-tiny-up' : 'jbr-tiny-down'}`}
                                            title={`${row.total} total jobs, ${row.delta >= 0 ? 'up' : 'down'} ${Math.abs(row.delta)}% ${WINDOW_DELTA_HINT[row.label] || 'vs the prior period of equal length'}`}
                                        >
                                            {row.delta >= 0 ? '▲' : '▼'} {Math.abs(row.delta)}%
                                            <span className="jbr-tiny-delta-suffix">vs prior</span>
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="jbr-table-footnote">
                    Deltas compare each window to the immediately preceding window of equal length. "This month" and "Last
                    month" compare to the same span one calendar month earlier.
                </p>
            </div>
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  Section 3 — Engagement                                             */
/* ------------------------------------------------------------------ */

function EngagementSection() {
    const [range, setRange] = useState('This Month');
    const [customRange, setCustomRange] = useState(null);

    const handleApplyCustom = (startISO, endISO) => {
        const start = parseISODate(startISO);
        const end = parseISODate(endISO);
        setCustomRange({
            start,
            end,
            startISO,
            endISO,
            label: start.getTime() === end.getTime() ? fmtShort(start) : `${fmtShort(start)} – ${fmtShort(end)}`,
        });
    };

    const stats = useMemo(() => {
        if (range === 'Custom Date' && customRange) {
            return computeCustomRangeStats(customRange.start, customRange.end, DAILY_INTAKE);
        }
        return computeRangeStats(range === 'Custom Date' ? 'This Month' : range, DAILY_INTAKE);
    }, [range, customRange]);

    const rangeDisplayLabel = range === 'Custom Date' && customRange ? customRange.label : range;
    const spark = (base, variance) => buildSparkline(Math.max(base, 1), Math.max(variance, 1));

    const chartGranularity = useMemo(() => {
        if (range === 'Last Month') return 'week';
        if (range === 'This Year' || range === 'Last Year') return 'month';
        if (range === 'Custom Date') {
            if (stats.dayCount > 120) return 'month';
            if (stats.dayCount > 31) return 'week';
        }
        return 'day';
    }, [range, stats.dayCount]);

    const engagementDaily = useMemo(() => {
        const base = stats.days.length > 0 ? buildEngagementDaily(stats.days) : buildEngagementDaily(DAILY_INTAKE.slice(-1));
        if (chartGranularity === 'month') return aggregateEngagementByMonth(base);
        if (chartGranularity === 'week') return aggregateEngagementByWeek(base);
        return base;
    }, [stats.days, chartGranularity]);

    const chartInterval = chartGranularity === 'day' ? Math.max(0, Math.ceil(engagementDaily.length / 6) - 1) : 0;
    const dayCount = Math.max(stats.dayCount, 1);

    // Funnel percentages recompute from the selected range's own totals, not a fixed snapshot.
    const saveRate = stats.views > 0 ? ((stats.saves / stats.views) * 100).toFixed(1) : '0.0';
    const applyRate = stats.saves > 0 ? ((stats.applications / stats.saves) * 100).toFixed(1) : '0.0';

    // Views/applications split by intake channel, proportional to that channel's
    // share of jobs this range — ties engagement back to where the job came from.
    const channelEngagement = useMemo(() => {
        const total = stats.current.total || 1;
        return ['apify', 'scrapper', 'bulk'].map((key) => {
            const share = stats.current[key] / total;
            const views = Math.round(stats.views * share);
            const applications = Math.round(stats.applications * share);
            return {
                key,
                label: CHANNEL[key].label,
                color: CHANNEL[key].color,
                jobs: stats.current[key],
                views,
                applications,
                conv: views > 0 ? `${((applications / views) * 100).toFixed(1)}%` : '—',
            };
        });
    }, [stats]);

    const openJob = (jobId) => window.open(`/job-board/view-job/${jobId}`, '_blank');

    return (
        <Section>
            <div className="jbr-section-head-row jbr-intake-head-row">
                <p className="jbr-showing-line jbr-showing-line-inline">
                    Showing <strong>{rangeDisplayLabel}</strong> · {stats.dateRangeText} · {stats.dayCount} day{stats.dayCount === 1 ? '' : 's'}
                </p>
                <RangeDropdown value={range} customRange={customRange} onSelect={setRange} onApplyCustom={handleApplyCustom} />
            </div>

            <div className="jbr-stat-grid">
                <StatCard icon={faCircleCheck} iconBg="rgba(199,43,122,0.1)" iconColor="#C72B7A" label="Total views" value={stats.views.toLocaleString()} delta={stats.viewsDelta} spark={spark(stats.views / dayCount, 150)} sparkColor="#C72B7A" />
                <StatCard icon={faBriefcase} iconBg="rgba(31,90,166,0.1)" iconColor="#1F5AA6" label="Jobs saved" value={stats.saves.toLocaleString()} delta={stats.savesDelta} spark={spark(stats.saves / dayCount, 40)} sparkColor="#1F5AA6" />
                <StatCard icon={faBriefcase} iconBg="rgba(184,121,12,0.12)" iconColor="#B8790C" label="Applications" value={stats.applications.toLocaleString()} delta={stats.applicationsDelta} spark={spark(stats.applications / dayCount, 20)} sparkColor="#B8790C" />
                <StatCard icon={faCircleCheck} iconBg="rgba(199,43,122,0.1)" iconColor="#C72B7A" label="View → apply rate" value={`${stats.rate.toFixed(1)}%`} delta={stats.rateDelta} spark={spark(stats.rate, 2)} sparkColor="#C72B7A" />
            </div>

            <div className="jbr-grid-2 jbr-grid-2-wide">
                <div className="jbr-card">
                    <h3 className="jbr-card-title">Engagement funnel</h3>
                    <p className="jbr-card-sub">{rangeDisplayLabel} · {stats.jobs.toLocaleString()} jobs posted</p>

                    <div className="jbr-funnel">
                        <div className="jbr-funnel-row">
                            <div className="jbr-funnel-bar jbr-funnel-bar-1">Views</div>
                            <div className="jbr-funnel-nums">
                                <span className="jbr-funnel-value">{stats.views.toLocaleString()}</span>
                                <span className="jbr-funnel-label">Total</span>
                            </div>
                        </div>
                        <span className="jbr-funnel-pct">{saveRate}%</span>

                        <div className="jbr-funnel-row">
                            <div className="jbr-funnel-bar jbr-funnel-bar-2" style={{ width: `${Math.max(Math.min(saveRate, 100), 4)}%` }}>Saved</div>
                            <div className="jbr-funnel-nums">
                                <span className="jbr-funnel-value">{stats.saves.toLocaleString()}</span>
                                <span className="jbr-funnel-label">Bookmarked</span>
                            </div>
                        </div>
                        <span className="jbr-funnel-pct">{applyRate}%</span>

                        <div className="jbr-funnel-row">
                            <div className="jbr-funnel-bar jbr-funnel-bar-3" style={{ width: `${Math.max(Math.min(applyRate, 100), 4)}%` }}>Applied</div>
                            <div className="jbr-funnel-nums">
                                <span className="jbr-funnel-value">{stats.applications.toLocaleString()}</span>
                                <span className="jbr-funnel-label">Applications</span>
                            </div>
                        </div>
                    </div>

                    <InfoNote>
                        Views, Saved and Applied each map straight to their own jobseeker-side action — a job view, a
                        bookmark, and a submitted application — so this funnel always reflects real on-platform intent,
                        not a proxy metric.
                    </InfoNote>
                </div>

                <div className="jbr-card">
                    <h3 className="jbr-card-title">Engagement by channel</h3>
                    <p className="jbr-card-sub">{rangeDisplayLabel} · views and applications by where the job came from</p>
                    <div className="jbr-table-scroll">
                        <table className="jbr-table">
                            <thead>
                                <tr>
                                    <th>Channel</th>
                                    <th>Jobs</th>
                                    <th>Views</th>
                                    <th>Applied</th>
                                    <th>Conv.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {channelEngagement.map((c) => (
                                    <tr key={c.key}>
                                        <td><ChannelPill channel={c.key} /></td>
                                        <td className="jbr-mono">{c.jobs.toLocaleString()}</td>
                                        <td className="jbr-mono">{c.views.toLocaleString()}</td>
                                        <td className="jbr-mono">{c.applications.toLocaleString()}</td>
                                        <td className="jbr-mono">{c.conv}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <InfoNote>
                        Split proportionally from each channel's share of jobs posted this range — once views/applications
                        are wired to real data, this reads directly off <strong>source_of_job</strong> instead.
                    </InfoNote>
                </div>
            </div>

            <div className="jbr-card">
                <h3 className="jbr-card-title">Engagement over time</h3>
                <p className="jbr-card-sub">
                    Views, saves and applications, {rangeDisplayLabel.toLowerCase()}
                    {chartGranularity !== 'day' && ` · ${chartGranularity}ly totals`} — each on its own scale so a smaller
                    metric never disappears next to a bigger one
                </p>
                <div className="jbr-trend-grid">
                    <EngagementTrendChart
                        title="Views"
                        dataKey="views"
                        color="#C72B7A"
                        gradientId="jbrEngFillViews"
                        data={engagementDaily}
                        total={stats.views}
                        avgLabel={`${Math.round(stats.views / dayCount).toLocaleString()}/day avg`}
                        chartInterval={chartInterval}
                    />
                    <EngagementTrendChart
                        title="Saved"
                        dataKey="saves"
                        color="#1F5AA6"
                        gradientId="jbrEngFillSaves"
                        data={engagementDaily}
                        total={stats.saves}
                        avgLabel={`${Math.round(stats.saves / dayCount).toLocaleString()}/day avg`}
                        chartInterval={chartInterval}
                    />
                    <EngagementTrendChart
                        title="Applications"
                        dataKey="applications"
                        color="#B8790C"
                        gradientId="jbrEngFillApps"
                        data={engagementDaily}
                        total={stats.applications}
                        avgLabel={`${Math.round(stats.applications / dayCount).toLocaleString()}/day avg`}
                        chartInterval={chartInterval}
                    />
                </div>
            </div>

            <div className="jbr-card">
                <div className="jbr-section-head-row">
                    <div>
                        <h3 className="jbr-card-title">Top performing jobs</h3>
                        <p className="jbr-card-sub">Ranked by on-platform applications, this month · click a job to open it</p>
                    </div>
                    <button
                        type="button"
                        className="jbr-btn jbr-btn-ghost"
                        onClick={() => {
                            // Clears any filter left over from a Job Inventory drill-through so "view all" is really all.
                            try { sessionStorage.removeItem('jobBoardAdminState'); } catch (err) {}
                            window.open('/job-board', '_blank');
                        }}
                    >
                        View all in job board <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 11 }} />
                    </button>
                </div>
                <div className="jbr-table-scroll">
                    <table className="jbr-table">
                        <thead>
                            <tr>
                                <th>Job</th>
                                <th>Channel</th>
                                <th>Views</th>
                                <th>Applied</th>
                                <th>Conv.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {TOP_JOBS.map((job) => (
                                <tr key={job.id} onClick={() => openJob(job.id)} style={{ cursor: 'pointer' }} title={`Open ${job.title} on the job board`}>
                                    <td>
                                        <span className="jbr-cell-title">{job.title}</span>
                                        <span className="jbr-cell-sub">{job.id} · {job.company}</span>
                                    </td>
                                    <td><ChannelPill channel={job.channel} /></td>
                                    <td className="jbr-mono">{job.views}</td>
                                    <td className="jbr-mono">{job.applied}</td>
                                    <td className="jbr-mono">{job.conv}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  Section 4 — Applications                                           */
/* ------------------------------------------------------------------ */

const APPLICATIONS_PAGE_SIZE = 10;

function ApplicationsSection() {
    const [range, setRange] = useState('This Month');
    const [customRange, setCustomRange] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(1);

    const handleApplyCustom = (startISO, endISO) => {
        const start = parseISODate(startISO);
        const end = parseISODate(endISO);
        setCustomRange({
            start,
            end,
            startISO,
            endISO,
            label: start.getTime() === end.getTime() ? fmtShort(start) : `${fmtShort(start)} – ${fmtShort(end)}`,
        });
        setPage(1);
    };

    const today = useMemo(() => stripTime(new Date()), []);
    const { start, end } = useMemo(() => {
        if (range === 'Custom Date' && customRange) return { start: customRange.start, end: customRange.end };
        const r = resolveWindow(range === 'Custom Date' ? 'This Month' : range, today);
        return { start: r.start, end: r.end };
    }, [range, customRange, today]);
    const rangeDisplayLabel = range === 'Custom Date' && customRange ? customRange.label : range;

    const inRange = useMemo(
        () => APPLICATIONS.filter((a) => a.appliedAt >= start && a.appliedAt <= end),
        [start, end]
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return inRange.filter((a) => {
            if (statusFilter !== 'all' && a.status !== statusFilter) return false;
            if (!q) return true;
            return (
                a.name.toLowerCase().includes(q) ||
                a.email.toLowerCase().includes(q) ||
                a.phone.toLowerCase().includes(q) ||
                a.jobTitle.toLowerCase().includes(q)
            );
        });
    }, [inRange, search, statusFilter]);

    const uniqueJobseekers = useMemo(() => new Set(inRange.map((a) => a.jobseekerId)).size, [inRange]);
    const uniqueJobs = useMemo(() => new Set(inRange.map((a) => a.jobId)).size, [inRange]);
    const avgPerJobseeker = uniqueJobseekers > 0 ? (inRange.length / uniqueJobseekers).toFixed(1) : '0.0';

    const pageCount = Math.max(1, Math.ceil(filtered.length / APPLICATIONS_PAGE_SIZE));
    const safePage = Math.min(page, pageCount);
    const pageRows = filtered.slice((safePage - 1) * APPLICATIONS_PAGE_SIZE, safePage * APPLICATIONS_PAGE_SIZE);

    const openJob = (jobId) => window.open(`/job-board/view-job/${jobId}`, '_blank');
    const openJobseeker = (jobseekerId) => window.open(`/user/jobseeker-details/${jobseekerId}`, '_blank');

    return (
        <Section>
            <div className="jbr-section-head-row jbr-intake-head-row">
                <p className="jbr-showing-line jbr-showing-line-inline">
                    Showing <strong>{rangeDisplayLabel}</strong> · {inRange.length.toLocaleString()} application{inRange.length === 1 ? '' : 's'}
                </p>
                <RangeDropdown
                    value={range}
                    customRange={customRange}
                    onSelect={(v) => { setRange(v); setPage(1); }}
                    onApplyCustom={handleApplyCustom}
                />
            </div>

            <div className="jbr-stat-grid">
                <StatCard icon={faBriefcase} iconBg="rgba(199,43,122,0.1)" iconColor="#C72B7A" label="Applications" value={inRange.length.toLocaleString()} footnote={rangeDisplayLabel} />
                <StatCard icon={faCircleCheck} iconBg="rgba(31,90,166,0.1)" iconColor="#1F5AA6" label="Jobseekers applied" value={uniqueJobseekers.toLocaleString()} footnote="Unique applicants" />
                <StatCard icon={faBriefcase} iconBg="rgba(184,121,12,0.12)" iconColor="#B8790C" label="Jobs applied to" value={uniqueJobs.toLocaleString()} footnote="Unique job postings" />
                <StatCard icon={faCircleCheck} iconBg="rgba(199,43,122,0.1)" iconColor="#C72B7A" label="Avg per jobseeker" value={avgPerJobseeker} footnote="Applications each" />
            </div>

            <div className="jbr-card">
                <h3 className="jbr-card-title">All applications</h3>
                <p className="jbr-card-sub">Every jobseeker application, across every job — click a job or a jobseeker to open it</p>

                <div className="jbr-filter-row">
                    <input
                        type="text"
                        className="jbr-search-input"
                        placeholder="Search name, email, phone, or job title…"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    />
                    <select
                        className="jbr-select-input"
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    >
                        <option value="all">All statuses</option>
                        {Object.entries(JOBSEEKER_STATUS).map(([key, s]) => (
                            <option key={key} value={key}>{s.label}</option>
                        ))}
                    </select>
                </div>

                {filtered.length === 0 ? (
                    <InfoNote>No applications match this filter. Try a wider date range or clear the search.</InfoNote>
                ) : (
                    <>
                        <div className="jbr-table-scroll">
                            <table className="jbr-table">
                                <thead>
                                    <tr>
                                        <th>Job</th>
                                        <th>Channel</th>
                                        <th>Jobseeker</th>
                                        <th>Phone</th>
                                        <th>Email</th>
                                        <th>Status</th>
                                        <th>Applied</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageRows.map((a) => (
                                        <tr key={a.id}>
                                            <td>
                                                <span
                                                    className="jbr-cell-title jbr-link-cell"
                                                    onClick={() => openJob(a.jobId)}
                                                    title={`Open ${a.jobTitle} on the job board`}
                                                >
                                                    {a.jobTitle}
                                                </span>
                                                <span className="jbr-cell-sub">{a.jobId}</span>
                                            </td>
                                            <td><ChannelPill channel={a.channel} /></td>
                                            <td>
                                                <span
                                                    className="jbr-cell-title jbr-link-cell"
                                                    onClick={() => openJobseeker(a.jobseekerId)}
                                                    title={`Open ${a.name}'s jobseeker profile`}
                                                >
                                                    {a.name}
                                                </span>
                                                <span className="jbr-cell-sub">{a.jobseekerId}</span>
                                            </td>
                                            <td className="jbr-mono">{a.phone}</td>
                                            <td className="jbr-mono">{a.email}</td>
                                            <td><JobseekerStatusPill status={a.status} /></td>
                                            <td className="jbr-mono">{fmtShort(a.appliedAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="jbr-pagination-row">
                            <span className="jbr-pagination-info">
                                {(safePage - 1) * APPLICATIONS_PAGE_SIZE + 1}–{Math.min(safePage * APPLICATIONS_PAGE_SIZE, filtered.length)} of {filtered.length}
                            </span>
                            <div className="jbr-pagination-btns">
                                <button type="button" className="jbr-btn jbr-btn-ghost jbr-btn-sm" disabled={safePage <= 1} onClick={() => setPage((p) => p - 1)}>
                                    Prev
                                </button>
                                <span className="jbr-pagination-info">Page {safePage} of {pageCount}</span>
                                <button type="button" className="jbr-btn jbr-btn-ghost jbr-btn-sm" disabled={safePage >= pageCount} onClick={() => setPage((p) => p + 1)}>
                                    Next
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  Section 5 — Network                                                */
/* ------------------------------------------------------------------ */

const NETWORK_PAGE_SIZE = 10;
const STALE_DAYS_THRESHOLD = 60;

function NetworkSection() {
    const openCompany = (dbId) => window.open(`/recruiter/vendor-company/view/${dbId}`, '_blank');
    const openRecruiter = (dbId) => window.open(`/recruiter/it/view/${dbId}`, '_blank');

    const [companySearch, setCompanySearch] = useState('');
    const [companySort, setCompanySort] = useState('jobs_desc');
    const [companyPage, setCompanyPage] = useState(1);

    const [recruiterSearch, setRecruiterSearch] = useState('');
    const [recruiterSort, setRecruiterSort] = useState('activity');
    const [confidenceFilter, setConfidenceFilter] = useState('all');
    const [recruiterPage, setRecruiterPage] = useState(1);

    const totalCompanies = VENDOR_COMPANIES.length;
    const totalRecruiters = RECRUITERS.length;
    const thirtyDaysAgo = useMemo(() => addDays(stripTime(new Date()), -30), []);
    const newCompanies30 = useMemo(() => VENDOR_COMPANIES.filter((c) => c.createdAt >= thirtyDaysAgo).length, [thirtyDaysAgo]);
    const newRecruiters30 = useMemo(() => RECRUITERS.filter((r) => r.createdAt >= thirtyDaysAgo).length, [thirtyDaysAgo]);
    const highConfidenceCount = useMemo(() => RECRUITERS.filter((r) => r.accuracy === 'High').length, []);
    const mediumConfidenceCount = useMemo(() => RECRUITERS.filter((r) => r.accuracy === 'Medium').length, []);
    const lowConfidenceCount = totalRecruiters - highConfidenceCount - mediumConfidenceCount;
    const highConfidencePct = totalRecruiters > 0 ? Math.round((highConfidenceCount / totalRecruiters) * 100) : 0;
    const staleRecruiterCount = useMemo(() => RECRUITERS.filter((r) => r.lastActivityDays > STALE_DAYS_THRESHOLD).length, []);
    const emailSourcedJobs = useMemo(() => VENDOR_COMPANIES.reduce((sum, c) => sum + c.jobs, 0), []);

    const companyGrowth = useMemo(() => buildGrowthWeekly(VENDOR_COMPANIES, 12), []);
    const recruiterGrowth = useMemo(() => buildGrowthWeekly(RECRUITERS, 12), []);

    const filteredCompanies = useMemo(() => {
        const q = companySearch.trim().toLowerCase();
        const rows = (q
            ? VENDOR_COMPANIES.filter((c) => c.name.toLowerCase().includes(q) || c.domain.toLowerCase().includes(q))
            : VENDOR_COMPANIES.slice()
        );
        return rows.sort((a, b) => {
            switch (companySort) {
                case 'recruiters_desc': return b.recruiters - a.recruiters;
                case 'applied_desc': return b.applied - a.applied;
                case 'newest': return b.createdAt - a.createdAt;
                case 'activity': return a.lastActivityDays - b.lastActivityDays;
                default: return b.jobs - a.jobs;
            }
        });
    }, [companySearch, companySort]);

    const filteredRecruiters = useMemo(() => {
        const q = recruiterSearch.trim().toLowerCase();
        let rows = (q
            ? RECRUITERS.filter((r) => r.name.toLowerCase().includes(q) || r.company.toLowerCase().includes(q))
            : RECRUITERS.slice()
        );
        if (confidenceFilter !== 'all') rows = rows.filter((r) => r.accuracy === confidenceFilter);
        return rows.sort((a, b) => {
            switch (recruiterSort) {
                case 'confidence': return b.pct - a.pct;
                case 'newest': return b.createdAt - a.createdAt;
                default: return a.lastActivityDays - b.lastActivityDays;
            }
        });
    }, [recruiterSearch, recruiterSort, confidenceFilter]);

    const companyPageCount = Math.max(1, Math.ceil(filteredCompanies.length / NETWORK_PAGE_SIZE));
    const companySafePage = Math.min(companyPage, companyPageCount);
    const companyRows = filteredCompanies.slice((companySafePage - 1) * NETWORK_PAGE_SIZE, companySafePage * NETWORK_PAGE_SIZE);

    const recruiterPageCount = Math.max(1, Math.ceil(filteredRecruiters.length / NETWORK_PAGE_SIZE));
    const recruiterSafePage = Math.min(recruiterPage, recruiterPageCount);
    const recruiterRows = filteredRecruiters.slice((recruiterSafePage - 1) * NETWORK_PAGE_SIZE, recruiterSafePage * NETWORK_PAGE_SIZE);

    return (
        <Section>
            <div className="jbr-stat-grid">
                <StatCard icon={faBriefcase} iconBg="rgba(199,43,122,0.1)" iconColor="#C72B7A" label="Vendor companies" value={totalCompanies.toLocaleString()} footnote={`${newCompanies30} new in the last 30 days`} />
                <StatCard icon={faCircleCheck} iconBg="rgba(31,90,166,0.1)" iconColor="#1F5AA6" label="IT recruiters" value={totalRecruiters.toLocaleString()} footnote={`${newRecruiters30} new in the last 30 days`} />
                <StatCard icon={faBriefcase} iconBg="rgba(184,121,12,0.12)" iconColor="#B8790C" label="Email-sourced jobs" value={emailSourcedJobs.toLocaleString()} footnote="Only channel these tie to" />
                <StatCard icon={faCircleCheck} iconBg="rgba(199,43,122,0.1)" iconColor="#C72B7A" label="High-confidence contacts" value={`${highConfidencePct}%`} footnote="Of the recruiter directory" />
            </div>

            {staleRecruiterCount > 0 && (
                <div className="jbr-callout">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="jbr-callout-icon" />
                    <p>
                        <strong>{staleRecruiterCount} recruiters</strong> have had no activity in over {STALE_DAYS_THRESHOLD} days,
                        and <strong>{lowConfidenceCount} recruiters</strong> are still Low confidence, unverified.
                    </p>
                    <button
                        className="jbr-btn jbr-btn-ghost"
                        onClick={() => {
                            setConfidenceFilter('Low');
                            setRecruiterSort('activity');
                            document.getElementById('jbr-recruiter-directory')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                    >
                        Review directory <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 11 }} />
                    </button>
                </div>
            )}

            <div className="jbr-card">
                <h3 className="jbr-card-title">Network growth</h3>
                <p className="jbr-card-sub">New vendor companies and recruiters added, last 12 weeks — real first-seen dates, not a synthetic trend</p>
                <div className="jbr-grid-2">
                    <EngagementTrendChart
                        title="Vendor companies added"
                        dataKey="count"
                        color="#C72B7A"
                        gradientId="jbrNetFillCompanies"
                        data={companyGrowth}
                        total={companyGrowth.reduce((s, r) => s + r.count, 0)}
                        avgLabel={`${(companyGrowth.reduce((s, r) => s + r.count, 0) / companyGrowth.length).toFixed(1)}/week avg`}
                        chartInterval={1}
                    />
                    <EngagementTrendChart
                        title="Recruiters added"
                        dataKey="count"
                        color="#1F5AA6"
                        gradientId="jbrNetFillRecruiters"
                        data={recruiterGrowth}
                        total={recruiterGrowth.reduce((s, r) => s + r.count, 0)}
                        avgLabel={`${(recruiterGrowth.reduce((s, r) => s + r.count, 0) / recruiterGrowth.length).toFixed(1)}/week avg`}
                        chartInterval={1}
                    />
                </div>
            </div>

            <div className="jbr-card">
                <div className="jbr-section-head-row">
                    <div>
                        <h3 className="jbr-card-title">Vendor companies</h3>
                        <p className="jbr-card-sub">{totalCompanies} total · grows automatically as email-sourced jobs come in · click a company to open it</p>
                    </div>
                    <button type="button" className="jbr-btn jbr-btn-ghost" onClick={() => window.open('/recruiter/vendor-company', '_blank')}>
                        View all <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 11 }} />
                    </button>
                </div>

                <div className="jbr-filter-row">
                    <input
                        type="text"
                        className="jbr-search-input"
                        placeholder="Search company or domain…"
                        value={companySearch}
                        onChange={(e) => { setCompanySearch(e.target.value); setCompanyPage(1); }}
                    />
                    <select className="jbr-select-input" value={companySort} onChange={(e) => { setCompanySort(e.target.value); setCompanyPage(1); }}>
                        <option value="jobs_desc">Sort: Most jobs</option>
                        <option value="recruiters_desc">Sort: Most recruiters</option>
                        <option value="applied_desc">Sort: Most applied</option>
                        <option value="activity">Sort: Recent activity</option>
                        <option value="newest">Sort: Newest</option>
                    </select>
                </div>

                <div className="jbr-table-scroll">
                    <table className="jbr-table">
                        <thead>
                            <tr>
                                <th>Company</th>
                                <th>Domain</th>
                                <th>Source</th>
                                <th>Recruiters</th>
                                <th>Total jobs</th>
                                <th>Applied</th>
                                <th>Last activity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {companyRows.map((v) => (
                                <tr key={v.id} onClick={() => openCompany(v.dbId)} style={{ cursor: 'pointer' }} title={`Open ${v.name} on the vendor company directory`}>
                                    <td>
                                        <div className="jbr-entity-cell">
                                            <Avatar initials={v.initials} color={v.color} />
                                            <div>
                                                <span className="jbr-cell-title">{v.name}</span>
                                                <span className="jbr-cell-sub">{v.id}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="jbr-muted-cell">{v.domain}</td>
                                    <td className="jbr-muted-cell">{v.source}</td>
                                    <td className="jbr-mono">{v.recruiters}</td>
                                    <td className="jbr-mono">{v.jobs}</td>
                                    <td className="jbr-mono">{v.applied}</td>
                                    <td><RecencyBadge when={v.lastActivity} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="jbr-pagination-row">
                    <span className="jbr-pagination-info">
                        {(companySafePage - 1) * NETWORK_PAGE_SIZE + 1}–{Math.min(companySafePage * NETWORK_PAGE_SIZE, filteredCompanies.length)} of {filteredCompanies.length}
                    </span>
                    <div className="jbr-pagination-btns">
                        <button type="button" className="jbr-btn jbr-btn-ghost jbr-btn-sm" disabled={companySafePage <= 1} onClick={() => setCompanyPage((p) => p - 1)}>Prev</button>
                        <span className="jbr-pagination-info">Page {companySafePage} of {companyPageCount}</span>
                        <button type="button" className="jbr-btn jbr-btn-ghost jbr-btn-sm" disabled={companySafePage >= companyPageCount} onClick={() => setCompanyPage((p) => p + 1)}>Next</button>
                    </div>
                </div>

                <InfoNote>
                    <strong>Vendor companies</strong> is the email-parsed intake registry — it has no link to
                    Apify/scraped postings at all, so <strong>Total jobs</strong> and <strong>Applied</strong> above
                    count email-sourced jobs only. <strong>Source</strong> is currently hardcoded to "TekP
                    Intelligence" on every real record — it doesn't vary yet, so don't expect a real breakdown
                    there until the backend captures more than one source. <strong>Last activity</strong> is this
                    company's most recently active recruiter — there's no activity timestamp on the company
                    record itself.
                </InfoNote>
            </div>

            <div className="jbr-card" id="jbr-recruiter-directory">
                <div className="jbr-section-head-row">
                    <div>
                        <h3 className="jbr-card-title">IT recruiter directory</h3>
                        <p className="jbr-card-sub">{totalRecruiters} total · click a recruiter to open it</p>
                    </div>
                    <button type="button" className="jbr-btn jbr-btn-ghost" onClick={() => window.open('/recruiter/it-recruiter', '_blank')}>
                        View all <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 11 }} />
                    </button>
                </div>

                <div className="jbr-donut-row">
                    <Donut
                        size={126}
                        thickness={19}
                        centerValue={`${highConfidencePct}%`}
                        centerLabel="HIGH CONF."
                        segments={[
                            { label: 'High confidence', value: highConfidenceCount, color: STATUS.active },
                            { label: 'Medium', value: mediumConfidenceCount, color: '#B8790C' },
                            { label: 'Low', value: lowConfidenceCount, color: '#1F5AA6' },
                        ]}
                    />
                    <div className="jbr-legend-list">
                        <div className="jbr-legend-row">
                            <span className="jbr-dot" style={{ background: STATUS.active }} />
                            <span className="jbr-legend-name">High confidence</span>
                            <span className="jbr-legend-value">{highConfidenceCount}</span>
                        </div>
                        <div className="jbr-legend-row">
                            <span className="jbr-dot" style={{ background: '#B8790C' }} />
                            <span className="jbr-legend-name">Medium</span>
                            <span className="jbr-legend-value">{mediumConfidenceCount}</span>
                        </div>
                        <div className="jbr-legend-row">
                            <span className="jbr-dot" style={{ background: '#1F5AA6' }} />
                            <span className="jbr-legend-name">Low</span>
                            <span className="jbr-legend-value">{lowConfidenceCount}</span>
                        </div>
                    </div>
                </div>

                <div className="jbr-filter-row">
                    <input
                        type="text"
                        className="jbr-search-input"
                        placeholder="Search recruiter or company…"
                        value={recruiterSearch}
                        onChange={(e) => { setRecruiterSearch(e.target.value); setRecruiterPage(1); }}
                    />
                    <select className="jbr-select-input" value={confidenceFilter} onChange={(e) => { setConfidenceFilter(e.target.value); setRecruiterPage(1); }}>
                        <option value="all">All confidence levels</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                    </select>
                    <select className="jbr-select-input" value={recruiterSort} onChange={(e) => { setRecruiterSort(e.target.value); setRecruiterPage(1); }}>
                        <option value="activity">Sort: Recent activity</option>
                        <option value="confidence">Sort: Confidence</option>
                        <option value="newest">Sort: Newest</option>
                    </select>
                </div>

                {filteredRecruiters.length === 0 ? (
                    <InfoNote>No recruiters match this filter.</InfoNote>
                ) : (
                    <>
                        <div className="jbr-table-scroll">
                            <table className="jbr-table">
                                <thead>
                                    <tr>
                                        <th>Recruiter</th>
                                        <th>Company</th>
                                        <th>Confidence</th>
                                        <th>Last active</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recruiterRows.map((r) => (
                                        <tr key={r.id} onClick={() => openRecruiter(r.dbId)} style={{ cursor: 'pointer' }} title={`Open ${r.name} on the recruiter directory`}>
                                            <td>
                                                <div className="jbr-entity-cell">
                                                    <Avatar initials={r.initials} color={r.color} size={28} />
                                                    <div>
                                                        <span className="jbr-cell-title">{r.name}</span>
                                                        <span className="jbr-cell-sub">{r.id}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="jbr-muted-cell">{r.company}</td>
                                            <td>
                                                <div className="jbr-accuracy-cell">
                                                    <Bar
                                                        pct={r.pct}
                                                        color={r.accuracy === 'High' ? STATUS.active : r.accuracy === 'Medium' ? '#B8790C' : '#1F5AA6'}
                                                        tooltip={`${r.name} · ${r.pct}% contact confidence (${r.accuracy})`}
                                                    />
                                                    <span className="jbr-accuracy-label" style={{ color: r.accuracy === 'High' ? STATUS.active : r.accuracy === 'Medium' ? '#B8790C' : '#1F5AA6' }}>
                                                        {r.accuracy}
                                                    </span>
                                                </div>
                                            </td>
                                            <td><RecencyBadge when={r.last} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="jbr-pagination-row">
                            <span className="jbr-pagination-info">
                                {(recruiterSafePage - 1) * NETWORK_PAGE_SIZE + 1}–{Math.min(recruiterSafePage * NETWORK_PAGE_SIZE, filteredRecruiters.length)} of {filteredRecruiters.length}
                            </span>
                            <div className="jbr-pagination-btns">
                                <button type="button" className="jbr-btn jbr-btn-ghost jbr-btn-sm" disabled={recruiterSafePage <= 1} onClick={() => setRecruiterPage((p) => p - 1)}>Prev</button>
                                <span className="jbr-pagination-info">Page {recruiterSafePage} of {recruiterPageCount}</span>
                                <button type="button" className="jbr-btn jbr-btn-ghost jbr-btn-sm" disabled={recruiterSafePage >= recruiterPageCount} onClick={() => setRecruiterPage((p) => p + 1)}>Next</button>
                            </div>
                        </div>
                    </>
                )}

                <InfoNote>
                    <strong>Confidence</strong> reflects how sure we are of this recruiter's contact info, not their
                    performance — recruiters captured automatically from a real inbound email are always set High;
                    ones an admin adds by hand default to Low until verified.
                </InfoNote>
            </div>
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  Page shell                                                          */
/* ------------------------------------------------------------------ */

export default function JobboardReport() {
    const navigate = useNavigate();
    const [tab, setTab] = useState('digest');
    const { data: digest, loading: digestLoading, error: digestError } = useJobImportDigest();

    return (
        <div className="jbr-page">
            <div className="jbr-shell">
                <div className="jbr-shell-inner">
                    <header className="jbr-header">
                        <div className="jbr-header-main">
                            <Grid size={{ md: 12, xs: 12 }} container flexDirection="column" className="jobseekerhead">
                                <h3>JobBoard Report</h3>
                                <span>
                                    <b onClick={() => navigate('/dashboard')}>Dashboard</b>{' '}
                                    <FontAwesomeIcon className="angleright" icon={faAngleRight} /> Jobboard Report
                                </span>
                            </Grid>
                        </div>
                        <div className="jbr-header-actions">
                            <button className="jbr-btn jbr-btn-primary">
                                <FontAwesomeIcon icon={faDownload} /> Export report
                            </button>
                        </div>
                    </header>

                    {digest?.not_started_alert && (
                        <div className="jbr-alert-banner">
                            <span className="jbr-alert-banner-icon">
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                            </span>
                            <div className="jbr-alert-banner-text">
                                <p className="jbr-alert-banner-title">Today's Apify scraping hasn't started</p>
                                <p className="jbr-alert-banner-desc">
                                    The {DIGEST_SLOT_LABELS[digest.slots?.[0]?.slot] ?? 'first'} scheduled scrape is overdue with no run recorded yet. Check the scheduler and queue worker.
                                </p>
                            </div>
                        </div>
                    )}

                    <nav className="jbr-nav">
                        {SECTIONS.map((s) => (
                            <button key={s.id} className={`jbr-nav-tab ${tab === s.id ? 'jbr-nav-tab-active' : ''}`} onClick={() => setTab(s.id)}>
                                {s.label}
                            </button>
                        ))}
                    </nav>

                    <main className="jbr-content">
                        {tab === 'digest' && <DigestSection digest={digest} loading={digestLoading} error={digestError} />}
                        {tab === 'inventory' && <JobInventorySection />}
                        {tab === 'intake' && <JobIntakeSection />}
                        {tab === 'engagement' && <EngagementSection />}
                        {tab === 'applications' && <ApplicationsSection />}
                        {tab === 'network' && <NetworkSection />}
                    </main>
                </div>
            </div>
        </div>
    );
}